import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, User, Calendar, MapPin, Users, DollarSign,
  Phone, Mail, MessageSquare, Star, CheckCircle2, Clock,
  UserCheck, TrendingUp,
  AlertTriangle, Shield, Bot, Send, Loader2, X, ChevronDown,
  ChevronUp, PlusCircle, RefreshCw, Users2, Target, Bell,
  BellOff, ClipboardList, PhoneCall, UserPlus, Bookmark,
  BookmarkCheck, Activity, Link2, Info, Zap, CheckSquare,
  Square, MoreHorizontal, Check, Pencil, FileText, ShieldCheck,
  Sparkles, BellRing, CheckCheck, Minus, BookOpen,
  Calculator, AlertCircle, FileBarChart2, BarChart2, HelpCircle,
} from "lucide-react";

import {
  leadApi, agentApi, followUpApi, engageApi, imagineApi,
  conversationApi, followApi, taskApi, relatedApi, formatError,
  hasCapability, eventModelApi, recommendationApi, crmEstimateApi,
  crmComposerApi,
} from "../services/api";
import { useAuth } from "@/contexts/AuthContext";
import { leadStatusLabel, qualStatusLabel } from "../utils/leadStatusLabels";
import { AuditTrailPanel } from "../components/AuditTrailPanel";
import { AIActionCenter } from "../components/crm/AIActionCenter";
import EventRequirementsPanel from "../components/EventRequirementsPanel";
import ConciergeEnquiryTab from "../components/ConciergeEnquiryTab";
import { InterventionBanner } from "../components/InterventionBanner";

// ─── Test IDs ────────────────────────────────────────────────────────────────
const T = {
  header:          "lead-header",
  leadName:        "lead-name",
  statusPill:      "lead-status-pill",
  pathBar:         "lead-path-bar",
  followBtn:       "lead-follow-btn",
  unfollowMenu:    "lead-unfollow-menu",
  contactMenuBtn:  "lead-contact-menu-btn",
  contactCall:     "contact-channel-call",
  contactEmail:    "contact-channel-email",
  contactWhatsapp: "contact-channel-whatsapp",
  startPlanningBtn:"lead-start-planning-btn",
  linkedEventBtn:  "lead-linked-event-btn",
  tabOverview:     "tab-overview",
  tabActivity:     "tab-activity",
  tabEventIntent:  "tab-event-intent",
  tabRelated:      "tab-related",
  tabAIInsights:   "tab-ai-insights",
  tabEventBrief:   "tab-event-brief",
  addTaskBtn:      "activity-add-task-btn",
  logCallBtn:      "activity-log-call-btn",
  sendMessageBtn:  "activity-send-message-btn",
  scheduleFollowUp:"activity-schedule-followup-btn",
  addMeetingBtn:   "activity-add-meeting-btn",
  addNoteBtn:      "activity-add-note-btn",
  upcomingSection: "activity-upcoming",
  overdueSection:  "activity-overdue",
  recentSection:   "activity-recent",
  relatedAccount:  "related-account",
  relatedContact:  "related-contact",
  relatedOpp:      "related-opportunity",
  relatedEvent:    "related-event",
  duplicateStatus: "related-duplicate-status",
};

const STATUS_COLOR = {
  NEW:           "bg-[#E5E5E5]/40 text-[#444444] border-[#C9C9C9]",
  CONTACTED:     "bg-blue-500/20 text-[#0176D3] border-blue-500/40",
  QUALIFYING:    "bg-cyan-500/20 text-[#0176D3] border-cyan-500/40",
  QUALIFIED:     "bg-emerald-500/20 text-[#2E844A] border-emerald-500/40",
  POC_REVIEW:    "bg-[#F1ECFF] text-[#5E4DB2] border-[#D8CEF7]",
  CONVERTED:     "bg-teal-500/20 text-[#2E844A] border-teal-500/40",
  NURTURE:       "bg-amber-500/20 text-[#8C4B00] border-amber-500/40",
  DISQUALIFIED:  "bg-[#FCEBEC]/60 text-[#BA0517] border-[#BA0517]/20",
  CLOSED:        "bg-[#E5E5E5]/20 text-[#706E6B] border-[#C9C9C9]/40",
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LeadDetail() {
  // urlParam: whatever is in the URL (LEAD-0001 OR the internal UUID slug)
  const { leadId: urlParam } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // Capability gates — evaluated once from auth context
  const canAssignOwner   = hasCapability(currentUser, "crm.lead.assign");  // CRM_MANAGER / BUSINESS_OWNER
  const canClaimLead     = hasCapability(currentUser, "crm.lead.claim");   // RELATIONSHIP_MANAGER self-claim
  const canConvert       = hasCapability(currentUser, "crm.lead.convert");
  const canQualify       = hasCapability(currentUser, "crm.lead.qualify");
  const canChangeStatus  = hasCapability(currentUser, "crm.lead.update");
  // backward compat alias — remove once all callsites updated
  const canAssignPoc     = canAssignOwner;
  // leadId is the canonical MongoDB _id — starts as urlParam, updated after load
  const [leadId, setLeadId]       = useState(urlParam);
  const [lead, setLead]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [pathConfig, setPathConfig] = useState(null);
  const [users, setUsers]         = useState([]);
  const [tab, setTab]             = useState("activity");
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Contact channel menu
  const [showContactMenu, setShowContactMenu] = useState(false);
  // Modals
  const [showAssignPoc, setShowAssignPoc]       = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showEscalate, setShowEscalate]         = useState(false);
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [showAddTask, setShowAddTask]           = useState(false);
  const [showLogCall, setShowLogCall]           = useState(false);
  const [showSendMessage, setShowSendMessage]   = useState(false);
  const [showAddMeeting, setShowAddMeeting]     = useState(false);
  const [showAddNote, setShowAddNote]           = useState(false);
  const [convertLoading, setConvertLoading]     = useState(false);

  // CRM-AI-ACTION-02: AI recommendation dispatch state
  // modalPrefill: pre-filled context from a dispatched AI recommendation
  // pendingRecId: the rec_id to mark ACTIONED after modal confirms
  // aiCenterRefreshKey: incremented to force AIActionCenter reload
  const [modalPrefill, setModalPrefill]         = useState(null);
  const [pendingRecId, setPendingRecId]         = useState(null);
  const [aiCenterRefreshKey, setAiCenterRefreshKey] = useState(0);

  // Duplicate resolution context from start-planning
  const [resolutionCtx, setResolutionCtx]       = useState(null);
  // Handoff blocked context
  const [handoffBlockedCtx, setHandoffBlockedCtx] = useState(null);
  // Identity edit modal
  const [showEditIdentity, setShowEditIdentity] = useState(false);
  // Event Intent edit modal
  const [showEditIntent, setShowEditIntent]     = useState(false);
  // Event Intent validation modal
  const [showValidateIntent, setShowValidateIntent] = useState(false);

  // loadError tracks the HTTP failure so we can show accurate error states
  const [loadError, setLoadError] = useState(null);

  // load() depends on urlParam only so setLeadId() inside doesn't cause infinite loop
  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [leadRes, pathRes, usersRes] = await Promise.all([
        leadApi.get(urlParam),  // initial lookup — supports display-ID (LEAD-XXXX) OR slug
        engageApi.getLeadPathConfig(),
        engageApi.listUsers(),
      ]);
      setLead(leadRes.data);
      setLeadId(leadRes.data.id);  // canonical MongoDB _id for all subsequent calls
      setFollowing(leadRes.data.is_following || false);
      setPathConfig(pathRes.data);
      setUsers(usersRes.data || []);
    } catch (e) {
      const status = e?.response?.status || 0;
      const message =
        status === 404
          ? "Lead not found"
          : status === 403
          ? "You do not have permission to view this lead"
          : status >= 500
          ? "Unable to load lead — a server error occurred. Please try again or contact support."
          : formatError(e);
      setLoadError({ status, message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [urlParam]);  // NOT leadId state — avoids loop

  useEffect(() => { load(); }, [load]);

  const handleFollow = async () => {
    if (followLoading) return;
    setFollowLoading(true);
    try {
      if (following) {
        await followApi.unfollow("LEAD", leadId);
        setFollowing(false);
        toast.success("Unfollowed");
      } else {
        await followApi.follow("LEAD", leadId);
        setFollowing(true);
        toast.success("Following this lead");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setFollowLoading(false);
    }
  };

  const handleConvertToEvent = async (resolution = null) => {
    if (convertLoading) return;
    setConvertLoading(true);
    try {
      const { data } = await leadApi.startPlanning(leadId, resolution);
      if (data.handoff_blocked) {
        setHandoffBlockedCtx(data);
        setConvertLoading(false);
        return;
      }
      if (data.resolution_required) {
        setResolutionCtx(data);
        setConvertLoading(false);
        return;
      }
      setResolutionCtx(null);
      // CORE-04: Lead conversion now produces Opportunity only — no Event Draft yet.
      // Navigate to Opportunities list; Event Planning is triggered from CLOSED_WON Opportunity.
      if (data.idempotent) {
        toast.info(`Already converted — Opportunity ${data.opportunity_number || data.opportunity_id} exists.`);
      } else {
        toast.success(`Lead converted. Opportunity ${data.opportunity_number || data.opportunity_id} created. Advance to CLOSED_WON to start Event Planning.`);
      }
      navigate(`/crm/opportunities`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setConvertLoading(false);
    }
  };

  const handleQualify = async () => {
    try {
      const { data } = await leadApi.qualify(leadId);
      toast.success(`Qualification: ${data.qualification?.status}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const handleClaimLead = async () => {
    try {
      await leadApi.claimLead(leadId);
      toast.success("Lead claimed — you are now the Relationship Manager.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  // ── CRM-AI-ACTION-02: Recommendation dispatch helpers ─────────────────────
  /**
   * After any modal created by an AI recommendation dispatches successfully,
   * this function marks the recommendation as ACTIONED in the backend and
   * triggers an AIActionCenter refresh to remove the rec from the active list.
   */
  const markRecActioned = async (recId, actionType, resultingRecordId = null) => {
    if (!recId) return;
    try {
      await recommendationApi.markActioned(leadId, recId, actionType, resultingRecordId);
    } catch {
      // Non-blocking — audit failure doesn't break CRM activity creation
    }
    setAiCenterRefreshKey(k => k + 1);
    setPendingRecId(null);
    setModalPrefill(null);
  };

  const dispatchFromRec = (prefill, openModal) => {
    setPendingRecId(prefill?.recId || null);
    setModalPrefill(prefill || null);
    openModal();
  };

  const handleDispatchCall     = (prefill) => dispatchFromRec(prefill, () => setShowLogCall(true));
  const handleDispatchScheduleCall = (prefill) => dispatchFromRec(prefill, () => setShowFollowUpModal(true));
  const handleDispatchMeeting  = (prefill) => dispatchFromRec(prefill, () => setShowAddMeeting(true));
  const handleDispatchFollowUp = (prefill) => dispatchFromRec(prefill, () => setShowFollowUpModal(true));
  const handleDispatchNote     = (prefill) => dispatchFromRec(prefill, () => setShowAddNote(true));
  const handleDispatchMessage  = (prefill) => dispatchFromRec(prefill, () => setShowSendMessage(true));

  // ── NBA Continue routing — deterministic, reuses existing modal/handler set ─
  const handleNBAContinue = useCallback((rec) => {
    if (!rec) return;
    const prefill = {
      recId:       rec.rec_id,
      title:       rec.title,
      notes:       rec.editable_description || rec.explanation || "",
      due_date:    rec.suggested_due_date || "",
      priority:    rec.priority || "MEDIUM",
      source:      "NBA_RECOMMENDATION",
      rec_rule_id: rec.rule_id,
    };
    // Rule-specific overrides take highest precedence
    if (rec.rule_id === "R02_ASSIGN_OWNER" || rec.rule_id?.includes("ASSIGN")) {
      setShowAssignPoc(true);
      return;
    }
    if (rec.rule_id === "R01_VALIDATE_EVENT_INTENT" || rec.rule_id?.includes("INTENT")) {
      setShowValidateIntent(true);
      return;
    }
    // Route by action type
    switch (rec.action_type) {
      case "CALL":      dispatchFromRec(prefill, () => setShowLogCall(true));     break;
      case "MEETING":   dispatchFromRec(prefill, () => setShowAddMeeting(true));  break;
      case "MESSAGE":   dispatchFromRec(prefill, () => setShowSendMessage(true)); break;
      case "NOTE":      dispatchFromRec(prefill, () => setShowAddNote(true));     break;
      case "FOLLOW_UP": dispatchFromRec(prefill, () => setShowFollowUpModal(true)); break;
      case "TASK":
      default:
        // Surface to Activity tab where AIActionCenter handles task creation
        setTab("activity");
        setAiCenterRefreshKey(k => k + 1);
        break;
    }
  }, []); // eslint-disable-line

  if (loading) return <LoadingState />;
  if (loadError) return <EmptyState message={loadError.message} onBack={() => navigate("/engage/leads")} />;

  const qs = lead.qualification_computed || {};

  return (
    <div className="h-full flex flex-col bg-[#F3F3F3] overflow-hidden">
      {/* Duplicate Resolution Modal */}
      {resolutionCtx && (
        <ResolutionModal
          ctx={resolutionCtx}
          onResolve={(selectedId) => {
            handleConvertToEvent({ type: resolutionCtx.resolution_type, selected_id: selectedId });
          }}
          onClose={() => setResolutionCtx(null)}
        />
      )}
      {/* Handoff Blocked Modal */}
      {handoffBlockedCtx && (
        <HandoffBlockedModal
          ctx={handoffBlockedCtx}
          onClose={() => setHandoffBlockedCtx(null)}
        />
      )}
      {/* Header */}
      <LeadRecordHeader
        lead={lead}
        following={following}
        followLoading={followLoading}
        showContactMenu={showContactMenu}
        onFollow={handleFollow}
        onToggleContactMenu={() => setShowContactMenu(v => !v)}
        onCloseContactMenu={() => setShowContactMenu(false)}
        onConvertToEvent={handleConvertToEvent}
        convertLoading={convertLoading}
        onBack={() => navigate("/engage/leads")}
        onQualify={handleQualify}
        canConvert={canConvert}
      />

      {/* Intake review banner for provisional Build My Event leads */}
      <IntakeReviewBanner lead={lead} />

      {/* CRM-UX-03: Business Owner Intervention Banner */}
      {leadId && (
        <div className="px-4 pt-2">
          <InterventionBanner recordType="lead" recordId={leadId} />
        </div>
      )}

      {/* Lead Path */}
      {pathConfig && (
        <LeadPathBar lead={lead} pathConfig={pathConfig} />
      )}

      {/* Mobile: NBA panel appears below progress path, above tabs */}
      <div className="lg:hidden border-b border-[#E5E5E5] bg-white overflow-y-auto max-h-72"
        data-testid="nba-mobile-container">
        <NextBestActionPanel
          leadId={leadId}
          lead={lead}
          refreshKey={activityRefreshKey}
          onContinue={handleNBAContinue}
        />
      </div>

      {/* ── Two-column layout: left = tab area, right = sticky NBA panel (desktop) ── */}
      <div className="flex flex-1 overflow-hidden min-h-0">

        {/* Left column — tab nav + tab content */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-[#E5E5E5] bg-white flex-shrink-0 overflow-x-auto">
            <div className="flex px-4 min-w-max">
              {[
                { id: "overview",     label: "Overview",     tid: T.tabOverview,    icon: Info },
                { id: "activity",     label: "Activity",     tid: T.tabActivity,    icon: Activity },
                { id: "event-intent", label: "Event Intent", tid: T.tabEventIntent, icon: Target },
                { id: "event-brief",  label: "Event Brief",  tid: T.tabEventBrief,  icon: BookOpen },
                { id: "related",      label: "Related",      tid: T.tabRelated,     icon: Link2 },
                { id: "ai-insights",  label: "AI Insights",  tid: T.tabAIInsights,  icon: Zap },
                ...(lead?.lead_source === "CONCIERGE_CHAT" ? [{ id: "concierge", label: "Concierge", tid: "tab-concierge", icon: MessageSquare }] : []),
              ].map(({ id, label, tid, icon: Icon }) => (
                <button
                  key={id}
                  data-testid={tid}
                  onClick={() => setTab(id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 whitespace-nowrap transition-colors ${
                    tab === id
                      ? "border-[#0176D3] text-[#0176D3] font-medium"
                      : "border-transparent text-[#706E6B] hover:text-[#444444] hover:border-[#C9C9C9]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto">
            {tab === "overview"     && <OverviewTab lead={lead} qs={qs} leadId={leadId} currentUser={currentUser} onAssignPoc={() => setShowAssignPoc(true)} onClaimLead={handleClaimLead} onQualify={handleQualify} onEditIdentity={() => setShowEditIdentity(true)} onSendMessage={() => setShowSendMessage(true)} canAssignPoc={canAssignOwner} canClaimLead={canClaimLead} canQualify={canQualify} onValidateIntent={() => setShowValidateIntent(true)} />}
            {tab === "activity"     && (
              <ActivityTab
                lead={lead}
                leadId={leadId}
                users={users}
                currentUser={currentUser}
                refreshKey={activityRefreshKey}
                onAddTask={() => setShowAddTask(true)}
                onLogCall={() => setShowLogCall(true)}
                onSendMessage={() => setShowSendMessage(true)}
                onScheduleFollowUp={() => setShowFollowUpModal(true)}
                onAddMeeting={() => setShowAddMeeting(true)}
                onAddNote={() => setShowAddNote(true)}
                onStatus={() => setShowStatusChange(true)}
                onEscalate={() => setShowEscalate(true)}
                canChangeStatus={canChangeStatus}
                onActivityCreated={() => setActivityRefreshKey(k => k + 1)}
                aiCenterRefreshKey={aiCenterRefreshKey}
                onDispatchCall={handleDispatchCall}
                onDispatchScheduleCall={handleDispatchScheduleCall}
                onDispatchMeeting={handleDispatchMeeting}
                onDispatchFollowUp={handleDispatchFollowUp}
                onDispatchNote={handleDispatchNote}
                onDispatchMessage={handleDispatchMessage}
              />
            )}
            {tab === "event-intent" && <EventIntentTab lead={lead} onRefresh={load} onEdit={() => setShowEditIntent(true)} onValidate={() => setShowValidateIntent(true)} currentUser={currentUser} />}
            {tab === "event-brief"  && (
              <div className="p-6">
                <EventRequirementsPanel leadId={leadId} />
              </div>
            )}
            {tab === "related"      && <RelatedTab lead={lead} leadId={leadId} />}
            {tab === "ai-insights"  && <AIInsightsTab lead={lead} qs={qs} />}
            {tab === "concierge"    && <ConciergeEnquiryTab lead={lead} />}
          </div>
        </div>{/* end left column */}

        {/* Right column — NBA panel, desktop only, sticky within scroll area */}
        <div
          className="hidden lg:flex flex-col w-72 xl:w-80 border-l border-[#E5E5E5] bg-white flex-shrink-0 overflow-y-auto"
          data-testid="nba-desktop-panel"
        >
          <NextBestActionPanel
            leadId={leadId}
            lead={lead}
            refreshKey={activityRefreshKey}
            onContinue={handleNBAContinue}
          />
        </div>

      </div>{/* end two-column */}

      {/* Modals */}
      {showAssignPoc    && <AssignPocModal users={users} leadId={leadId} onClose={() => setShowAssignPoc(false)} onDone={load} />}
      {showFollowUpModal && <FollowUpModal leadId={leadId} users={users} onClose={() => { setShowFollowUpModal(false); setModalPrefill(null); setPendingRecId(null); }} onDone={() => { load(); setTab("activity"); markRecActioned(pendingRecId, "FOLLOW_UP"); }} prefill={modalPrefill} />}
      {showEscalate     && <EscalateModal leadId={leadId} onClose={() => setShowEscalate(false)} onDone={load} />}
      {showStatusChange && <StatusChangeModal lead={lead} pathConfig={pathConfig} onClose={() => setShowStatusChange(false)} onDone={load} />}
      {showAddMeeting   && <AddMeetingModal leadId={leadId} onClose={() => { setShowAddMeeting(false); setModalPrefill(null); setPendingRecId(null); }} onDone={() => { setActivityRefreshKey(k => k + 1); markRecActioned(pendingRecId, "MEETING"); }} prefill={modalPrefill} />}
      {showAddNote      && <AddNoteModal leadId={leadId} onClose={() => { setShowAddNote(false); setModalPrefill(null); setPendingRecId(null); }} onDone={() => { setActivityRefreshKey(k => k + 1); markRecActioned(pendingRecId, "NOTE"); }} prefill={modalPrefill} />}
      {showEditIdentity && (
        <LeadIdentityModal
          lead={lead}
          onClose={() => setShowEditIdentity(false)}
          onUpdated={() => { setShowEditIdentity(false); load(); }}
        />
      )}
      {showEditIntent && (
        <EditEventIntentModal
          lead={lead}
          onClose={() => setShowEditIntent(false)}
          onUpdated={() => { setShowEditIntent(false); load(); setActivityRefreshKey(k => k + 1); }}
        />
      )}
      {showValidateIntent && (
        <ValidateIntentModal
          lead={lead}
          leadId={leadId}
          currentUser={currentUser}
          onClose={() => setShowValidateIntent(false)}
          onValidated={() => { setShowValidateIntent(false); load(); setActivityRefreshKey(k => k + 1); }}
        />
      )}
      {showAddTask      && <AddTaskModal leadId={leadId} users={users} onClose={() => setShowAddTask(false)} onDone={() => { setShowAddTask(false); setTab("activity"); setActivityRefreshKey(k => k + 1); }} />}
      {showLogCall      && <LogCallModal leadId={leadId} onClose={() => { setShowLogCall(false); setModalPrefill(null); setPendingRecId(null); }} onDone={() => { setShowLogCall(false); setTab("activity"); setActivityRefreshKey(k => k + 1); markRecActioned(pendingRecId, "CALL"); }} prefill={modalPrefill} />}
      {showSendMessage  && <SendMessageModal lead={lead} leadId={leadId} onClose={() => { setShowSendMessage(false); setModalPrefill(null); setPendingRecId(null); }} onDone={() => { load(); markRecActioned(pendingRecId, "MESSAGE"); }} prefill={modalPrefill} />}
    </div>
  );
}

// ─── Lead Record Header ───────────────────────────────────────────────────────
function LeadRecordHeader({ lead, following, followLoading, showContactMenu,
  onFollow, onToggleContactMenu, onCloseContactMenu, onConvertToEvent, convertLoading,
  onBack, onQualify, canConvert }) {

  const navigate = useNavigate();
  const menuRef  = useRef(null);

  // Close contact menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onCloseContactMenu();
    };
    if (showContactMenu) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showContactMenu, onCloseContactMenu]);

  // Derive available contact channels from lead data
  const channels = [];
  const phone = lead.customer_contact_phone || lead.phone || lead.contact_phone || "";
  const email = lead.customer_contact_email || lead.customer_email || lead.contact_email || "";
  const isWhatsApp = lead.source_channel === "WHATSAPP" ||
    lead.preferred_contact_channel === "WHATSAPP";
  if (phone)      channels.push({ id: "call",     label: "Call",     icon: PhoneCall, testId: T.contactCall,     href: `tel:${phone}`, value: phone });
  if (email)      channels.push({ id: "email",    label: "Email",    icon: Mail,      testId: T.contactEmail,    href: `mailto:${email}`, value: email });
  if (isWhatsApp && phone) channels.push({ id: "whatsapp", label: "WhatsApp", icon: MessageSquare, testId: T.contactWhatsapp,
    href: `https://wa.me/${phone.replace(/\D/g, "")}`, value: phone });

  const isBMELead = lead.lead_source === "BUILD_MY_EVENT";
  const eventInfo = [];
  if (lead.tentative_event_date || lead.event_date) eventInfo.push({ icon: Calendar, label: fmtDate(lead.tentative_event_date || lead.event_date) });
  const locRaw = lead.location || lead.event_location;
  const locDisplay = isBMELead ? (CITY_LABELS[locRaw] || locRaw) : locRaw;
  if (locDisplay) eventInfo.push({ icon: MapPin, label: locDisplay });
  if (lead.guest_count) eventInfo.push({ icon: Users, label: `${lead.guest_count} guests` });
  if (lead.budget_min || lead.budget_max || lead.budget_range) {
    const bud = lead.budget_range || [lead.budget_min, lead.budget_max].filter(Boolean).join(" – ");
    if (bud) eventInfo.push({ icon: DollarSign, label: bud });
  }

  return (
    <div data-testid={T.header} className="bg-white border-b border-[#E5E5E5] flex-shrink-0">
      <div className="px-4 pt-3 pb-2">
        {/* Breadcrumb row */}
        <div className="flex items-center gap-2 mb-2">
          <button onClick={onBack} className="text-[#706E6B] hover:text-[#444444] transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-[11px] font-mono text-[#706E6B]">
            {lead.lead_number || lead.id?.slice(0, 8)}
          </span>
          {lead.status && (
            <span data-testid={T.statusPill}
              className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_COLOR[lead.status] || "bg-[#E5E5E5]/40 text-[#444444] border-[#C9C9C9]"}`}>
              {leadStatusLabel(lead.status)}
            </span>
          )}
          {lead.qualification_status && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#EEF4FF] border border-[#0176D3]/20 text-[#706E6B]">
              {qualStatusLabel(lead.qualification_computed?.status || lead.qualification_status)}
            </span>
          )}
          {/* CRM-UX-01 Stage 05: Qualification provenance badge for converted leads with low score */}
          {lead.status === "CONVERTED" && lead.qualification_status && lead.qualification_status !== "QUALIFIED" && (
            <span
              data-testid="conversion-qualification-provenance"
              className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/30 text-[#8C4B00]"
              title={lead.conversion_qualification_snapshot
                ? `Converted at score ${lead.conversion_qualification_snapshot.score}% (${lead.conversion_qualification_snapshot.status}) on ${new Date(lead.conversion_qualification_snapshot.converted_at).toLocaleDateString()}`
                : "Lead was converted while qualification data was incomplete"}
            >
              Converted · Score {lead.qualification_score ?? 0}% at conversion
            </span>
          )}
          {lead.is_provisional && (
            <span data-testid="provisional-badge"
              className="text-[10px] font-bold tracking-[0.08em] uppercase px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30">
              PROVISIONAL
            </span>
          )}
          {lead.lead_source === "BUILD_MY_EVENT" && (
            <span data-testid="build-my-event-badge"
              className="text-[10px] font-bold tracking-[0.08em] uppercase px-2.5 py-0.5 rounded-full"
              style={{ background:"rgba(232,82,110,0.10)", color:"#D91E48", border:"1px solid rgba(217,30,72,0.30)" }}>
              BUILD MY EVENT
            </span>
          )}
        </div>

        {/* Name + type row */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 data-testid={T.leadName}
              className="text-xl font-semibold text-[#181818] font-sans truncate">
              {lead.customer_name || lead.contact_name || [lead.salutation, lead.first_name, lead.last_name].filter(Boolean).join(" ") || "—"}
            </h1>
            {(lead.event_type_interest || lead.event_type) && (
              <p className="text-sm text-[#0176D3] mt-0.5">
                {isBMELead ? (EVENT_TYPE_LABELS[lead.event_type_interest || lead.event_type] || lead.event_type_interest || lead.event_type) : (lead.event_type_interest || lead.event_type)}
                {lead.source_channel && (
                  <span className="ml-2 text-xs text-[#706E6B] font-mono">via {lead.source_channel}</span>
                )}
              </p>
            )}
          </div>

          {/* Primary actions */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {/* Follow / Following */}
            <FollowButton following={following} loading={followLoading} onToggle={onFollow} />

            {/* Contact menu */}
            <div className="relative" ref={menuRef}>
              <button
                data-testid={T.contactMenuBtn}
                onClick={onToggleContactMenu}
                disabled={channels.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] rounded-lg text-xs text-[#444444] hover:border-[#0176D3]/30 hover:text-[#0176D3] transition-colors disabled:opacity-40">
                <Phone className="w-3.5 h-3.5" />
                Contact
                <ChevronDown className="w-3 h-3" />
              </button>
              {showContactMenu && channels.length > 0 && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg shadow-xl min-w-[160px] py-1">
                  {channels.map(ch => (
                    <a key={ch.id} href={ch.href} target={ch.id !== "call" ? "_blank" : undefined}
                      rel="noreferrer"
                      data-testid={ch.testId}
                      onClick={onCloseContactMenu}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-[#444444] hover:bg-[#EEF4FF] hover:text-white transition-colors">
                      <ch.icon className="w-3.5 h-3.5 text-[#0176D3]" />
                      <div>
                        <div>{ch.label}</div>
                        <div className="text-[10px] text-[#706E6B] truncate max-w-[130px]">{ch.value}</div>
                      </div>
                    </a>
                  ))}
                  {channels.length === 0 && (
                    <p className="px-3 py-2 text-xs text-[#706E6B]">No contact channels available</p>
                  )}
                </div>
              )}
              {showContactMenu && channels.length === 0 && (
                <div className="absolute right-0 top-full mt-1 z-20 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg shadow-xl min-w-[160px] py-2 px-3 text-xs text-[#706E6B]">
                  No contact channels available
                </div>
              )}
            </div>

            {/* Start Event Planning / Linked Event */}
            {lead.linked_event_id ? (
              <button
                data-testid={T.linkedEventBtn}
                onClick={() => navigate(`/imagine/events/${lead.linked_event_id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium">
                <Calendar className="w-3.5 h-3.5" /> Open Event
              </button>
            ) : canConvert ? (
              <button
                data-testid={T.startPlanningBtn}
                onClick={() => onConvertToEvent()}
                disabled={convertLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] disabled:opacity-60 transition-colors font-medium">
                {convertLoading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <PlusCircle className="w-3.5 h-3.5" />}
                Start Event Planning
              </button>
            ) : null}
          </div>
        </div>

        {/* Contact / event info chips */}
        {(phone || email || eventInfo.length > 0) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
            {phone && (
              <span className="flex items-center gap-1 text-xs text-[#706E6B]">
                <Phone className="w-3 h-3 text-[#706E6B]" /> {phone}
              </span>
            )}
            {email && (
              <span className="flex items-center gap-1 text-xs text-[#706E6B] truncate max-w-[200px]">
                <Mail className="w-3 h-3 text-[#706E6B]" /> {email}
              </span>
            )}
            {eventInfo.map((item, i) => (
              <span key={i} className="flex items-center gap-1 text-xs text-[#706E6B]">
                <item.icon className="w-3 h-3" /> {item.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Follow Button ─────────────────────────────────────────────────────────────
function FollowButton({ following, loading, onToggle }) {
  const [showMenu, setShowMenu] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMenu]);

  if (!following) {
    return (
      <button
        data-testid={T.followBtn}
        onClick={onToggle}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] rounded-lg text-xs text-[#706E6B] hover:border-slate-500 hover:text-[#181818] disabled:opacity-60 transition-colors">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />}
        Follow
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={T.followBtn}
        onClick={() => setShowMenu(v => !v)}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] disabled:opacity-60 transition-colors">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookmarkCheck className="w-3.5 h-3.5" />}
        Following
        <ChevronDown className="w-3 h-3" />
      </button>
      {showMenu && (
        <div data-testid={T.unfollowMenu}
          className="absolute right-0 top-full mt-1 z-20 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg shadow-xl min-w-[120px] py-1">
          <button
            onClick={() => { setShowMenu(false); onToggle(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-400 hover:bg-rose-500/10 transition-colors">
            <BellOff className="w-3.5 h-3.5" /> Unfollow
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Lead Path Bar ─────────────────────────────────────────────────────────────
function LeadPathBar({ lead, pathConfig }) {
  const primaryPath = pathConfig?.primary_path || [];
  const terminalStatuses = pathConfig?.terminal_statuses || [];
  const alternateStatuses = pathConfig?.alternate_statuses || [];

  const currentCode = lead.status || "";
  const isTerminal = terminalStatuses.includes(currentCode);
  const isAlternate = alternateStatuses.includes(currentCode);

  const currentIdx = primaryPath.findIndex(s => s.code === currentCode);
  const isOnPrimary = currentIdx >= 0;

  return (
    <div data-testid={T.pathBar}
      className="bg-[#F3F3F3] border-b border-[#1C2740] px-4 py-3 flex-shrink-0 overflow-x-auto">
      <div className="flex items-center gap-0 min-w-max">
        {primaryPath.map((step, idx) => {
          const isDone    = isOnPrimary ? idx < currentIdx : false;
          const isCurrent = isOnPrimary && idx === currentIdx;
          const isFuture  = !isOnPrimary || idx > currentIdx;

          return (
            <div key={step.code} className="flex items-center">
              {/* Step node */}
              <div className={`flex flex-col items-center gap-1 px-3 py-0.5 rounded-lg transition-all ${
                isCurrent ? "bg-[#EEF4FF]" : ""
              }`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                  isDone    ? "bg-emerald-500 border-emerald-500" :
                  isCurrent ? "bg-[#0176D3] border-[#0176D3]" :
                  "bg-transparent border-[#E5E5E5]"
                }`}>
                  {isDone ? (
                    <Check className="w-3 h-3 text-white" />
                  ) : (
                    <div className={`w-2 h-2 rounded-full ${isCurrent ? "bg-white" : "bg-[#E5E5E5]"}`} />
                  )}
                </div>
                <span className={`text-[10px] font-medium whitespace-nowrap ${
                  isDone    ? "text-[#2E844A]" :
                  isCurrent ? "text-[#0176D3]" :
                  "text-[#706E6B]"
                }`}>
                  {step.label}
                </span>
              </div>
              {/* Connector */}
              {idx < primaryPath.length - 1 && (
                <div className={`h-px w-6 ${isDone ? "bg-emerald-500/50" : "bg-[#E5E5E5]"}`} />
              )}
            </div>
          );
        })}

        {/* Alternate/terminal state badge */}
        {(isTerminal || isAlternate) && (
          <div className="ml-4 flex items-center gap-1.5 px-3 py-1 rounded-full border border-amber-500/40 bg-amber-500/10">
            <AlertTriangle className="w-3 h-3 text-[#8C4B00]" />
            <span className="text-[10px] text-[#8C4B00] font-mono">{currentCode}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CRM-INT-02: Journey label lookups ────────────────────────────────────────
const SOURCE_LABELS = {
  CUSTOMER_CONFIRMED:      "Customer Selected",
  SYSTEM_DEFAULT:          "Suggested Default",
  TEMPLATE_DEFAULT:        "Default suggestion",
  INSPIRATION_SUGGESTED:   "Inspiration pick",
  AGENT_RECOMMENDED:       "Recommended",
  COMMUNICATION_EXTRACTED: "From conversation",
  SYSTEM_DERIVED:          "System derived",
};
const EVENT_TYPE_LABELS = {
  WEDDING:"Wedding", ENGAGEMENT:"Engagement", BIRTHDAY:"Birthday Party",
  CORPORATE:"Corporate Event", CONFERENCE:"Conference", ANNIVERSARY:"Anniversary",
  SOCIAL:"Social Gathering",
};
const CITY_LABELS = {
  HYDERABAD:"Hyderabad", BENGALURU:"Bengaluru", CHENNAI:"Chennai",
  MUMBAI:"Mumbai", DELHI:"Delhi NCR", OTHER:"Other",
};
const GUEST_LABELS = {
  UNDER_100:"Under 100","100_250":"100 – 250","250_500":"250 – 500",
  "500_750":"500 – 750","750_1000":"750 – 1,000","1000_PLUS":"1,000+",NOT_SURE:"TBD",
};
const BUDGET_LABELS = {
  UNDER_10L:"Under ₹10L","10L_20L":"₹10L – ₹20L","20L_35L":"₹20L – ₹35L",
  "35L_50L":"₹35L – ₹50L","50L_PLUS":"₹50L+",DISCUSS:"To discuss",
};
const VENUE_LABELS = {
  CONFIRMED:"Confirmed",SHORTLISTED:"Shortlisting",
  SEARCHING:"Not yet decided",HELP_REQUIRED:"Need recommendations",
};
const SERVICE_LABELS = {
  VENUE:"Venue",DECOR:"Decor & Styling",CATERING:"Food & Catering",
  PHOTOGRAPHY:"Photography",AV_PRODUCTION:"AV & Production",
  ENTERTAINMENT:"Entertainment",GUEST_HOSPITALITY:"Guest Hospitality",
  TRANSPORT:"Transport",INVITATIONS:"Invitations & RSVP",
};
const STYLE_LABELS = {
  ELEGANT:"Elegant",TRADITIONAL:"Traditional",CONTEMPORARY:"Contemporary",
  MINIMAL:"Minimal",FLORAL:"Floral",LUXURY:"Luxury",VIBRANT:"Vibrant",NOT_SURE:"Not sure yet",
};
const FUNCTION_LABELS = {
  ENGAGEMENT:"Engagement",HALDI:"Haldi",MEHENDI:"Mehendi",
  SANGEETH:"Sangeet",WEDDING:"Wedding",RECEPTION:"Reception",OTHER:"Other",
};
const PATH_LABELS = {
  WEBSITE:"Website", DATE_CHECK:"Date Check", BUILD_MY_EVENT:"Build My Event",
  DIRECT:"Direct", WHATSAPP:"WhatsApp",
  // EXP-01E: inspiration + matcher paths
  FIND_EVENTS_LIKE_MINE: "Event Matcher",
  MATCH_RESULTS:          "Match Results",
  EVENT_STORY:            "Event Story",
  PLAN_ONE_LIKE_THIS:     "Plan One Like This",
  INSPIRATION_GALLERY:    "Inspiration Gallery",
  EXPLORE_GALLERY:        "Explore Gallery",
};

// ─── Intake Review Banner ─────────────────────────────────────────────────────
function IntakeReviewBanner({ lead }) {
  const validation   = lead.event_intent_validation;
  const isProvisional = lead.is_provisional;
  const isValidated  = validation?.status === "VALIDATED";

  // Show for provisional BME leads awaiting review, or any lead that has a validation record
  if (!isProvisional && !validation) return null;

  if (isValidated) {
    return (
      <div data-testid="intake-review-banner"
        className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-200 flex items-center gap-2.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-emerald-800">Event Intent Validated</span>
        <span className="text-xs text-emerald-600 hidden sm:inline">
          — Reviewed by {validation.validated_by_email || validation.validated_by}
        </span>
      </div>
    );
  }
  if (validation?.status === "CLARIFICATION_REQUIRED") {
    return (
      <div data-testid="intake-review-banner"
        className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-amber-800">Clarification Required</span>
        <span className="text-xs text-amber-600 hidden sm:inline">— Further event details needed before proceeding.</span>
      </div>
    );
  }
  if (validation?.status === "NOT_PROCEEDING") {
    return (
      <div data-testid="intake-review-banner"
        className="px-4 py-2.5 bg-red-50 border-b border-red-200 flex items-center gap-2.5">
        <X className="w-4 h-4 text-red-600 flex-shrink-0" />
        <span className="text-xs font-semibold text-red-800">Not Proceeding</span>
        <span className="text-xs text-red-600 hidden sm:inline">— Lead marked as not proceeding by reviewer.</span>
      </div>
    );
  }
  // Default: provisional lead awaiting review
  return (
    <div data-testid="intake-review-banner"
      className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2.5">
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <span className="text-xs font-semibold text-amber-800">Event Intent Review Required</span>
      <span className="text-xs text-amber-600 hidden sm:inline">
        — Build My Event journey complete. Validate intent details before progressing.
      </span>
    </div>
  );
}

// ─── Journey Story Panel (light premium — EXP-01H redesign) ─────────────────
function JourneyStoryPanel({ lead }) {
  // Show for BME leads, inspiration-origin leads, or leads with a rich journey path
  // Data-driven: check actual canonical fields, not just one string flag
  const isBMELead          = lead.lead_source === "BUILD_MY_EVENT";
  const hasInspirationData = !!(lead.event_intent?.inspiration_slug);
  const hasJourneyPath     = (lead.journey_path || []).length > 0;
  if (!isBMELead && !hasInspirationData && !hasJourneyPath) return null;

  const ei   = lead.event_intent           || {};
  const prov = lead.event_intent_provenance || {};
  const stylePref  = ei.style_preferences  || [];
  const serviceInt = ei.service_interests  || [];
  const eventFuncs = ei.event_functions    || [];
  const isWedding  = (ei.event_type || "").toUpperCase() === "WEDDING";
  const completionPct = lead.intent_completion_pct ?? 0;
  const journeyPath   = lead.journey_path || [];
  const channel    = lead.source_channel    || "";
  const touchpoint = lead.source_touchpoint || "";
  const entryPoint = lead.source_entry_point;

  // Light premium palette (NO dark panels anywhere)
  const ivory     = "#FBF7F2";
  const sand      = "#FAF6F0";
  const gold      = "#C9A15F";
  const ruby      = "#C64040";
  const charcoal  = "#3A3330";
  const slate     = "#7A706A";
  const pGreen    = { bg:"#E8F5EC", text:"#2E7D4F", border:"rgba(46,125,79,0.25)" };
  const pAmber    = { bg:"#FEF3C7", text:"#92400E", border:"rgba(146,64,14,0.20)" };
  const pGold     = { bg:`${gold}12`, text:"#92600A", border:`${gold}30` };
  const divBorder = `1px solid rgba(201,161,95,0.18)`;

  const fmtEDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"long", year:"numeric" }); }
    catch { return d; }
  };

  const intentRows = [
    { key:"event_type",        label:"Event",          value: EVENT_TYPE_LABELS[ei.event_type] || ei.event_type },
    { key:"event_date",        label:"Event Date",     value: fmtEDate(ei.event_date) },
    { key:"event_city",        label:"Location",       value: CITY_LABELS[ei.event_city] || ei.event_city },
    { key:"guest_count_range", label:"Guests",         value: GUEST_LABELS[ei.guest_count_range] || ei.guest_count_range },
    { key:"planning_scope",    label:"Planning Scope", value: ei.planning_scope === "END_TO_END" ? "End-to-End" : ei.planning_scope },
    { key:"venue_status",      label:"Venue",          value: VENUE_LABELS[ei.venue_status] || ei.venue_status },
    { key:"budget_range",      label:"Budget",         value: BUDGET_LABELS[ei.budget_range] || ei.budget_range },
  ].filter(r => r.value);

  return (
    <div data-testid="journey-story-panel"
      className="rounded-2xl overflow-hidden"
      style={{ background: ivory, border: "1px solid rgba(201,161,95,0.22)", fontFamily:"'DM Sans',system-ui,sans-serif" }}>

      {/* Header */}
      <div className="px-5 pt-5 pb-3.5" style={{ borderBottom: divBorder }}>
        <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color: gold }}>
          {isBMELead ? "Build My Event Journey" : hasInspirationData ? "Inspiration Journey" : "Customer Journey"}
        </p>
        <p className="text-xs mt-0.5" style={{ color: slate }}>
          Customer-confirmed event intent
        </p>
      </div>

      {/* Inspired By section (EXP-01H) — shown when inspiration_slug is present */}
      {ei.inspiration_slug && (
        <div data-testid="journey-inspired-by" className="px-5 py-4" style={{ borderBottom: divBorder }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-3" style={{ color: gold }}>Inspired By</p>
          <div className="rounded-xl p-3.5" style={{ background: pGold.bg, border: `1px solid ${pGold.border}` }}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-snug" style={{ color: charcoal }}>
                  {ei.inspiration_title || ei.inspiration_slug}
                </p>
                <p className="text-[10px] mt-0.5" style={{ color: slate }}>
                  Portfolio inspiration
                  {lead.journey_path?.includes("PLAN_ONE_LIKE_THIS") && " · Plan One Like This"}
                </p>
              </div>
              <a
                href={`/explore/${ei.inspiration_slug}`}
                target="_blank" rel="noreferrer"
                className="text-[10px] flex items-center gap-1 flex-shrink-0 hover:underline mt-0.5"
                style={{ color: gold, fontWeight: 500 }}
              >
                View story <ArrowUpRight className="w-3 h-3" />
              </a>
            </div>
            {/* Style tags from inspiration (if any confirmed style prefs exist) */}
            {stylePref.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2.5">
                {stylePref.map(s => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(201,161,95,0.14)", border: `1px solid ${gold}35`, color: "#92600A" }}>
                    {STYLE_LABELS[s] || s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Event Intent */}
      {intentRows.length > 0 && (
        <div className="px-5 py-4" style={{ borderBottom: divBorder }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-3" style={{ color: gold }}>Event Intent</p>
          <div className="space-y-3">
            {intentRows.map(({ key, label, value }) => {
              const p = prov[key];
              const isConfirmed = p?.confirmed === true;
              const isReview    = p?.source === "INSPIRATION_SUGGESTED" && !isConfirmed;
              return (
                <div key={key} className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: slate }}>{label}</p>
                    <p className="text-sm font-medium" style={{ color: charcoal }}>{value}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {isConfirmed && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.06em] px-2 py-0.5 rounded-full"
                        style={{ background: pGreen.bg, color: pGreen.text, border: `1px solid ${pGreen.border}` }}>
                        Customer Selected
                      </span>
                    )}
                    {isReview && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.06em] px-2 py-0.5 rounded-full"
                        style={{ background: pAmber.bg, color: pAmber.text, border: `1px solid ${pAmber.border}` }}>
                        Review
                      </span>
                    )}
                    {p && !isConfirmed && !isReview && (
                      <span className="text-[9px] italic" style={{ color: slate }}>
                        {SOURCE_LABELS[p.source] || p.source}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Journey Completion */}
      {isBMELead && (
        <div className="px-5 py-4" style={{ borderBottom: divBorder }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: gold }}>Journey Completion</p>
            <span className="text-sm font-bold" style={{ color: charcoal }}>{completionPct}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"rgba(201,161,95,0.12)" }}>
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width:`${completionPct}%`, background: completionPct >= 80 ? pGreen.text : gold }} />
          </div>
        </div>
      )}

      {/* Acquisition Source */}
      <div className="px-5 py-4" style={{ borderBottom: divBorder }}>
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-3" style={{ color: gold }}>Acquisition Source</p>
        <p className="text-sm font-medium mb-2.5" style={{ color: charcoal }}>
          {[PATH_LABELS[channel]||channel, PATH_LABELS[touchpoint]||touchpoint].filter(Boolean).join(" — ")}
        </p>
        {journeyPath.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {journeyPath.map((step, i) => {
              const isLast = i === journeyPath.length - 1;
              return (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="text-[11px] px-2.5 py-0.5 rounded-full"
                    style={{
                      background: isLast ? `${ruby}12` : sand,
                      border: `1px solid ${isLast ? `${ruby}35` : "rgba(201,161,95,0.20)"}`,
                      color: isLast ? ruby : slate,
                      fontWeight: isLast ? 600 : 400,
                    }}>
                    {PATH_LABELS[step] || step}
                  </span>
                  {!isLast && (
                    <ArrowRight className="w-3 h-3 flex-shrink-0" style={{ color:"rgba(201,161,95,0.40)" }} />
                  )}
                </div>
              );
            })}
          </div>
        )}
        {entryPoint && (
          <p className="text-[10px] mt-2" style={{ color: slate }}>
            Entry: <span style={{ color: charcoal, fontWeight: 500 }}>{PATH_LABELS[entryPoint] || entryPoint}</span>
          </p>
        )}
      </div>

      {/* Wedding Functions */}
      {isWedding && eventFuncs.length > 0 && (
        <div className="px-5 py-4" style={{ borderBottom: divBorder }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-3" style={{ color: gold }}>Wedding Functions</p>
          <div className="flex flex-wrap gap-1.5">
            {eventFuncs.map(f => (
              <span key={f} className="text-xs px-3 py-1 rounded-full"
                style={{ background: `${ruby}0f`, border: `1px solid ${ruby}30`, color: ruby }}>
                {FUNCTION_LABELS[f] || f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Services */}
      {serviceInt.length > 0 && (
        <div className="px-5 py-4" style={{ borderBottom: divBorder }}>
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-3" style={{ color: gold }}>Services</p>
          <div className="flex flex-wrap gap-1.5">
            {serviceInt.map(s => (
              <span key={s} className="text-xs px-3 py-1 rounded-full"
                style={{ background: sand, border: "1px solid rgba(201,161,95,0.22)", color: charcoal }}>
                {SERVICE_LABELS[s] || s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Style Direction — only if inspiration_slug NOT present (already shown in Inspired By) */}
      {stylePref.length > 0 && !ei.inspiration_slug && (
        <div className="px-5 py-4">
          <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: gold }}>Style Direction</p>
          <p style={{ fontFamily:"'Cormorant Garamond',Georgia,serif", fontStyle:"italic", color: charcoal, fontSize:"1.05rem", lineHeight:1.4 }}>
            {stylePref.map(s => STYLE_LABELS[s] || s).join(" · ")}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Journey Summary (ActivityTab — factual, no fabricated timestamps) ────────
function JourneySummarySection({ lead }) {
  if (lead.lead_source !== "BUILD_MY_EVENT") return null;
  const ei   = lead.event_intent           || {};
  const prov = lead.event_intent_provenance || {};
  const captured = [
    ei.event_type        && { key:"event_type",        label:"Event type",   value: EVENT_TYPE_LABELS[ei.event_type] || ei.event_type },
    ei.event_date        && { key:"event_date",        label:"Date",         value: fmtDate(ei.event_date) },
    ei.event_city        && { key:"event_city",        label:"Location",     value: CITY_LABELS[ei.event_city] || ei.event_city },
    ei.guest_count_range && { key:"guest_count_range", label:"Guests",       value: GUEST_LABELS[ei.guest_count_range] || ei.guest_count_range },
    ei.budget_range      && { key:"budget_range",      label:"Budget",       value: BUDGET_LABELS[ei.budget_range] || ei.budget_range },
    ei.planning_scope    && { key:"planning_scope",    label:"Scope",        value: ei.planning_scope === "END_TO_END" ? "End-to-End" : ei.planning_scope },
  ].filter(Boolean);
  if (captured.length === 0) return null;
  return (
    <div data-testid="journey-summary-section"
      className="mb-4 rounded-xl overflow-hidden"
      style={{ background:"#1A1410", border:"1px solid rgba(201,161,95,0.22)", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <div className="px-4 py-3" style={{ borderBottom:"1px solid rgba(201,161,95,0.12)" }}>
        <p className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color:"#C9A15F" }}>Journey Origin</p>
        <p className="text-xs mt-0.5" style={{ color:"rgba(255,255,255,0.5)" }}>
          {lead.intent_completion_pct ?? 0}% of Build My Event completed
        </p>
      </div>
      <div className="px-4 py-3">
        <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color:"#6B6055" }}>What the customer told us</p>
        <div className="space-y-1.5">
          {captured.map(({ key, label, value }) => (
            <div key={key} className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] w-20 flex-shrink-0" style={{ color:"#6B6055" }}>{label}</span>
              <span className="text-xs" style={{ color:"rgba(255,255,255,0.78)" }}>{value}</span>
              {prov[key]?.confirmed && (
                <span className="text-[9px] px-1.5 py-0.5 rounded"
                  style={{ background:"rgba(53,101,77,0.18)", color:"#5BAD7A", border:"1px solid rgba(53,101,77,0.28)" }}>confirmed</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Recommended Actions Panel (dark, capability-gated) ──────────────────────
// ─── NBA Stage Help — deterministic contextual guidance per lead status ──────
const NBA_STAGE_HELP = {
  NEW:           { stage: "New Lead",        summary: "Needs owner assignment, event intent confirmation, and initial discovery contact.", milestone: "Complete the first discovery call and qualify all core event details." },
  CONTACTED:     { stage: "Contacted",       summary: "Initial contact made. Capture full event requirements via a structured discovery meeting.", milestone: "Confirm event scope, venue, date, and budget — then qualify the lead." },
  QUALIFYING:    { stage: "Qualifying",      summary: "Gathering qualification evidence. Confirm all key event details and scoring criteria.", milestone: "Complete qualification scoring and advance to QUALIFIED status." },
  QUALIFIED:     { stage: "Qualified",       summary: "Requirements confirmed and scored. Prepare and present a formal proposal.", milestone: "Deliver proposal and secure customer sign-off." },
  POC_REVIEW:    { stage: "POC Review",      summary: "Awaiting business owner or manager review and approval.", milestone: "Obtain POC approval to advance to proposal stage." },
  PROPOSAL_SENT: { stage: "Proposal Sent",   summary: "Proposal shared. Follow up within 48 hours to address objections and close.", milestone: "Receive customer commitment or schedule a final review meeting." },
  WON:           { stage: "Lead Won",        summary: "Customer committed. Convert this lead to an active Event record.", milestone: "Complete handoff brief and assign the planning team." },
  CONVERTED:     { stage: "Converted",       summary: "This lead has been converted to an active Event.", milestone: "Access the Event record for planning and execution." },
  NURTURE:       { stage: "Nurturing",       summary: "Customer not ready now. Maintain periodic contact to stay top-of-mind.", milestone: "Log a scheduled follow-up within the next 30 days." },
  DISQUALIFIED:  { stage: "Disqualified",    summary: "Lead did not meet qualification criteria.", milestone: "Review disqualification reason before closing or re-opening." },
  CLOSED:        { stage: "Closed",          summary: "Lead has been closed.", milestone: "No further action required unless re-engaged by customer." },
  LOST:          { stage: "Lost",            summary: "Lead lost to competitor or abandoned.", milestone: "Document loss reason and consider re-engagement after 60 days." },
};

const NBA_ACTION_ICONS = {
  CALL: PhoneCall, MEETING: Calendar, TASK: ClipboardList,
  FOLLOW_UP: Clock, MESSAGE: Send, NOTE: FileText, DEFAULT: Zap,
};

const NBA_PRIORITY_STYLE = {
  CRITICAL: { bg:"#FFF1F0", text:"#E7251F", border:"rgba(231,37,31,0.3)"  },
  HIGH:     { bg:"#FFFBEA", text:"#8C4B00", border:"rgba(201,100,0,0.3)"  },
  MEDIUM:   { bg:"#EEF4FF", text:"#0176D3", border:"rgba(1,118,211,0.3)"  },
  LOW:      { bg:"#F9F9F9", text:"#706E6B", border:"rgba(112,110,107,0.3)" },
};

// ─── Next Best Action Panel ───────────────────────────────────────────────────
// Deterministic workflow-driven NBA panel.
// Fetches existing recommendations (useAi=false), surfaces primary + 3 secondary
// actions, routes Continue to the correct existing modal via onContinue callback.
// No AI, no new API, no new data model.
function NextBestActionPanel({ leadId, lead, refreshKey = 0, onContinue }) {
  const [recs,    setRecs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);

  const status = (lead?.status || "NEW").toUpperCase();
  const help   = NBA_STAGE_HELP[status] || null;

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await recommendationApi.getRecommendations(leadId, false);
      setRecs((res.data?.recommendations || []).slice(0, 4));
    } catch {
      setRecs([]);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (refreshKey > 0) load(); }, [refreshKey]);

  const primary   = recs[0] || null;
  const secondary = recs.slice(1, 4);

  const gold     = "#C9A15F";
  const ivory    = "#FBF7F2";
  const charcoal = "#3A3330";
  const slate    = "#7A706A";
  const divider  = "rgba(201,161,95,0.18)";

  if (loading) return (
    <div className="p-4 space-y-3" data-testid="nba-loading">
      {[1,2,3].map(i => (
        <div key={i} className="h-10 rounded-lg bg-[#F3F3F3] animate-pulse" />
      ))}
    </div>
  );

  const PrimaryIcon = primary ? (NBA_ACTION_ICONS[primary.action_type] || NBA_ACTION_ICONS.DEFAULT) : Zap;
  const badge = primary ? (NBA_PRIORITY_STYLE[primary.priority] || NBA_PRIORITY_STYLE.MEDIUM) : null;

  return (
    <div
      data-testid="next-best-action-panel"
      style={{ fontFamily: "'DM Sans',system-ui,sans-serif" }}
    >
      {/* Panel header */}
      <div
        className="px-4 py-3 flex-shrink-0"
        style={{ borderBottom:`1px solid ${divider}`, background: ivory }}
      >
        <p className="text-[10px] font-bold tracking-[0.14em] uppercase"
          style={{ color: gold }}>Next Best Action</p>
        {help && (
          <p className="text-xs mt-0.5 font-medium" style={{ color: charcoal }}>
            {help.stage}
          </p>
        )}
      </div>

      {/* Primary action card */}
      {primary ? (
        <div
          data-testid="nba-primary-action"
          className="p-4"
          style={{ background:"#FFFDF9", borderBottom:`1px solid ${divider}` }}
        >
          <div className="flex items-center justify-between mb-3">
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
              style={{ background:badge.bg, color:badge.text, borderColor:badge.border }}
            >
              {primary.priority}
            </span>
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background:`${gold}18` }}
            >
              <PrimaryIcon className="w-3.5 h-3.5" style={{ color: gold }} />
            </div>
          </div>

          <p
            data-testid="nba-primary-title"
            className="font-semibold leading-snug mb-2"
            style={{ color:charcoal, fontFamily:"'Cormorant Garamond',serif", fontSize:"0.95rem" }}
          >
            {primary.title}
          </p>

          <p className="text-[11px] leading-relaxed mb-3" style={{ color: slate }}>
            {primary.explanation}
          </p>

          {(primary.source_evidence || []).length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1"
                style={{ color: gold }}>Basis</p>
              <div className="space-y-1">
                {primary.source_evidence.slice(0, 3).map((ev, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <div className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0"
                      style={{ background: gold }} />
                    <p className="text-[11px] font-mono break-all" style={{ color: slate }}>{ev}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {primary.suggested_timing && (
            <p className="text-[10px] mb-3" style={{ color: slate }}>
              <span style={{ color: gold }}>When: </span>{primary.suggested_timing}
            </p>
          )}

          <button
            data-testid="nba-continue-btn"
            onClick={() => onContinue?.(primary)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ background: gold, color:"#fff" }}
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="p-5 text-center" data-testid="nba-all-clear">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2"
            style={{ color: gold, opacity: 0.5 }} />
          <p className="text-xs font-medium" style={{ color: charcoal }}>
            All actions complete
          </p>
          <p className="text-[11px] mt-1" style={{ color: slate }}>
            No pending actions for this stage.
          </p>
        </div>
      )}

      {/* Secondary actions */}
      {secondary.length > 0 && (
        <div style={{ borderBottom:`1px solid ${divider}` }}>
          <p className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-[0.12em] uppercase"
            style={{ color: slate }}>Also consider</p>
          {secondary.map((rec) => {
            const SecIcon = NBA_ACTION_ICONS[rec.action_type] || NBA_ACTION_ICONS.DEFAULT;
            return (
              <button
                key={rec.rec_id}
                data-testid={`nba-secondary-${rec.rule_id}`}
                onClick={() => onContinue?.(rec)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FBF7F2] transition-colors"
                style={{ borderTop:`1px solid ${divider}` }}
              >
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background:`${gold}12` }}
                >
                  <SecIcon className="w-3.5 h-3.5" style={{ color: gold }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: charcoal }}>{rec.title}</p>
                  {rec.suggested_timing && (
                    <p className="text-[10px] truncate" style={{ color: slate }}>{rec.suggested_timing}</p>
                  )}
                </div>
                <ArrowRight className="w-3 h-3 flex-shrink-0" style={{ color:`${gold}70` }} />
              </button>
            );
          })}
        </div>
      )}

      {/* Stage guide */}
      {help && (
        <div>
          <button
            data-testid="nba-help-toggle"
            onClick={() => setHelpOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FBF7F2] transition-colors"
          >
            <div className="flex items-center gap-2">
              <HelpCircle className="w-3.5 h-3.5" style={{ color: gold }} />
              <p className="text-xs font-medium" style={{ color: charcoal }}>Stage Guide</p>
            </div>
            {helpOpen
              ? <ChevronUp   className="w-3.5 h-3.5" style={{ color: slate }} />
              : <ChevronDown className="w-3.5 h-3.5" style={{ color: slate }} />
            }
          </button>
          {helpOpen && (
            <div className="px-4 pb-4 space-y-2" style={{ background: ivory }}>
              <p className="text-[11px] leading-relaxed" style={{ color: charcoal }}>
                {help.summary}
              </p>
              <div
                className="flex items-start gap-2 p-2.5 rounded-lg"
                style={{ background:`${gold}10`, border:`1px solid ${gold}25` }}
              >
                <Target className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: gold }} />
                <p className="text-[11px] font-medium leading-relaxed" style={{ color: charcoal }}>
                  {help.milestone}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Recommended Actions Panel (legacy — kept in Overview tab) ────────────────
function RecommendedActionsPanel({ lead, currentUser, onClaimLead, onQualify, onSendMessage, onAssignPoc, onValidateIntent }) {
  const canClaim   = hasCapability(currentUser, "crm.lead.claim");
  const canAssign  = hasCapability(currentUser, "crm.lead.assign");
  const canQualify = hasCapability(currentUser, "crm.lead.qualify") || hasCapability(currentUser, "crm.lead.manage");
  const canUpdate  = hasCapability(currentUser, "crm.lead.update") || hasCapability(currentUser, "crm.lead.manage");
  const phone      = lead.customer_contact_phone || lead.contact_phone || "";

  const actions = [
    canClaim && !lead.owner_user_id && {
      id:"claim",   label:"Claim This Lead",
      desc:"Assign yourself as Relationship Manager",
      icon:UserCheck, onClick:onClaimLead, primary:true,
    },
    canAssign && {
      id:"assign", label: lead.owner_user_id ? "Change Relationship Manager" : "Assign Relationship Manager",
      desc:"Select or change the RM for this lead",
      icon:Users2, onClick:onAssignPoc,
    },
    canUpdate && {
      id:"review",  label:"Validate Event Intent",
      desc:"Confirm the customer's event details are accurate",
      icon:CheckCircle2, onClick:onValidateIntent,
    },
    phone && {
      id:"contact", label:"Contact Customer", desc:phone,
      icon:PhoneCall, href:`tel:${phone}`,
    },
    canUpdate && {
      id:"message", label:"Send Welcome Message",
      desc:"Connect via available channels",
      icon:Send, onClick:onSendMessage,
    },
    canQualify && lead.status !== "QUALIFIED" && {
      id:"qualify", label:"Qualify Lead",
      desc:"Run or re-run qualification scoring",
      icon:TrendingUp, onClick:onQualify,
    },
  ].filter(Boolean);

  if (actions.length === 0) return null;
  return (
    <div data-testid="recommended-actions-panel"
      className="rounded-2xl overflow-hidden"
      style={{ background:"#1A1410", border:"1px solid rgba(201,161,95,0.22)", fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      <div className="px-5 pt-4 pb-3" style={{ borderBottom:"1px solid rgba(201,161,95,0.12)" }}>
        <p className="text-[10px] font-bold tracking-[0.14em] uppercase" style={{ color:"#C9A15F" }}>Recommended Actions</p>
      </div>
      <div>
        {actions.map((action, i) => {
          const commonStyle = {
            borderColor:"rgba(201,161,95,0.10)",
            background: action.primary ? "rgba(201,161,95,0.06)" : "transparent",
          };
          const inner = (
            <>
              <action.icon className="w-4 h-4 flex-shrink-0"
                style={{ color: action.primary ? "#C9A15F" : "rgba(255,255,255,0.42)" }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: action.primary ? "#C9A15F" : "rgba(255,255,255,0.82)" }}>
                  {action.label}
                </p>
                {action.desc && (
                  <p className="text-[10px] mt-0.5 truncate" style={{ color:"rgba(255,255,255,0.32)" }}>{action.desc}</p>
                )}
              </div>
            </>
          );
          const cls = `w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${i < actions.length-1 ? "border-b" : ""}`;
          return action.href
            ? <a key={action.id} href={action.href} className={cls} style={commonStyle}>{inner}</a>
            : <button key={action.id} data-testid={action.id === "review" ? "validate-intent-btn" : undefined} onClick={action.onClick} className={cls} style={commonStyle}>{inner}</button>;
        })}
      </div>
    </div>
  );
}

// ─── Raw Intent (Debug Details — internal/admin use only, behind auth) ───────
function RawIntentDetails({ lead }) {
  const [open, setOpen] = useState(false);
  if (!lead.event_intent && !lead.event_intent_provenance) return null;
  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F3F3F3] transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[#706E6B]">Debug Details</span>
          <span className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Internal Only</span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-[#706E6B]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#706E6B]" />}
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-[#E5E5E5] bg-[#F3F3F3]">
          <p className="text-[10px] text-amber-700 font-semibold mt-3 mb-1">
            For authorized administrators only. Not for sharing with customers.
          </p>
          <p className="text-[10px] text-[#706E6B] uppercase tracking-wider mt-3 mb-1">Event Intent Payload</p>
          <pre className="text-[10px] text-[#444444] font-mono overflow-x-auto whitespace-pre-wrap break-all">
            {JSON.stringify(lead.event_intent, null, 2)}
          </pre>
          {lead.event_intent_provenance && (
            <>
              <p className="text-[10px] text-[#706E6B] uppercase tracking-wider mt-3 mb-1">Field Provenance Map</p>
              <pre className="text-[10px] text-[#444444] font-mono overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(lead.event_intent_provenance, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Compact Journey Timeline (right column Overview — EXP-01H) ───────────────
// Shows the chronological business story of this lead (readable narrative).
// Technically separate from AuditTrailPanel (immutable evidence).
const JOURNEY_ITEM_META = {
  LEAD_CREATED:              { label: "Lead Created",              color: "#0176D3" },
  PROVISIONAL_LEAD_CREATED:  { label: "Enquiry Received",          color: "#2E7D4F" },
  LEAD_ASSIGNED:             { label: "RM Assigned",               color: "#0176D3" },
  LEAD_CLAIMED:              { label: "Lead Claimed",              color: "#0176D3" },
  LEAD_STATUS_CHANGED:       { label: "Status Changed",            color: "#706E6B" },
  LEAD_QUALIFIED:            { label: "Qualification Updated",     color: "#2E7D4F" },
  LEAD_CONVERTED:            { label: "Lead Converted",            color: "#2E7D4F" },
  NOTE_ADDED:                { label: "Note Added",                color: "#706E6B" },
  FOLLOW_UP:                 { label: "Follow-up Scheduled",       color: "#C9A15F" },
  TASK_CREATED:              { label: "Task Created",              color: "#706E6B" },
  TASK_COMPLETED:            { label: "Task Completed",            color: "#2E7D4F" },
  MEETING:                   { label: "Meeting Logged",            color: "#0176D3" },
  CALL:                      { label: "Call Logged",               color: "#0176D3" },
  OWNERSHIP_MIGRATION_PARC01R: { label: "System Migration",       color: "#9B59B6" },
};

function getJourneyItemMeta(activityType) {
  const m = JOURNEY_ITEM_META[activityType];
  if (m) return m;
  const label = (activityType || "Activity").replace(/_/g, " ").toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
  return { label, color: "#706E6B" };
}

function CompactJourneyTimeline({ leadId }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    conversationApi.getTimeline(leadId, { limit: 10 })
      .then(r => {
        const data = r.data;
        const list = Array.isArray(data) ? data : (data.timeline || []);
        setItems(list);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [leadId]);

  const gold     = "#C9A15F";
  const charcoal = "#3A3330";
  const slate    = "#7A706A";
  const ivory    = "#FBF7F2";
  const divider  = "rgba(201,161,95,0.15)";

  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-IN", { day:"numeric", month:"short" })
        + " · " + d.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", hour12:true });
    } catch { return iso; }
  };

  return (
    <div
      data-testid="compact-journey-timeline"
      className="rounded-xl overflow-hidden"
      style={{ background: ivory, border: `1px solid ${divider}` }}
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${divider}` }}>
        <Activity className="w-3.5 h-3.5" style={{ color: gold }} />
        <h3 className="text-sm font-semibold" style={{ color: charcoal }}>Customer Journey</h3>
      </div>

      {/* Body — max 240px with scroll */}
      <div style={{ maxHeight: "240px", overflowY: "auto" }}>
        {loading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" style={{ color: slate }} />
            <span className="text-xs" style={{ color: slate }}>Loading…</span>
          </div>
        )}
        {error && (
          <p className="text-xs px-4 py-4" style={{ color: slate }}>Unable to load journey.</p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-xs px-4 py-4" style={{ color: slate }}>No journey events yet.</p>
        )}
        {!loading && !error && items.map((item, i) => {
          const meta = getJourneyItemMeta(item.activity_type || item.type);
          const isLast = i === items.length - 1;
          return (
            <div
              key={item.id || i}
              className="flex items-start gap-3 px-4 py-2.5"
              style={{ borderBottom: isLast ? "none" : `1px solid ${divider}` }}
            >
              {/* Dot */}
              <div
                className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: meta.color }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-snug" style={{ color: charcoal }}>{meta.label}</p>
                {item.description && (
                  <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: slate, wordBreak: "break-word" }}>
                    {item.description}
                  </p>
                )}
                <p className="text-[10px] mt-0.5" style={{ color: slate }}>
                  {fmtTs(item.created_at || item.timestamp)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ lead, qs, leadId, currentUser, onAssignPoc, onClaimLead, onQualify, onEditIdentity, onSendMessage, canAssignPoc, canClaimLead, canQualify, onValidateIntent }) {
  const score = qs.score ?? lead.qualification_score ?? 0;
  const isBME = lead.lead_source === "BUILD_MY_EVENT";

  const QualCard = () => (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[#181818] font-sans">Qualification</h3>
        {canQualify && (
          <button onClick={onQualify} className="flex items-center gap-1 text-xs text-[#706E6B] hover:text-[#0176D3] transition-colors">
            <RefreshCw className="w-3 h-3" /> Re-qualify
          </button>
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1.5">
          <span className={`text-xs font-mono px-2 py-0.5 rounded border ${
            score >= 75 ? "bg-emerald-500/15 text-[#2E844A] border-emerald-500/30" :
            score >= 40 ? "bg-amber-500/15 text-[#8C4B00] border-amber-500/30" :
            "bg-rose-500/15 text-rose-300 border-rose-500/30"
          }`}>{qualStatusLabel(qs.status || lead.qualification_status || "UNQUALIFIED")}</span>
          <span className="text-xs font-mono text-[#706E6B]">{score}%</span>
        </div>
        <div className="h-1.5 bg-[#F3F3F3] rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${
            score >= 75 ? "bg-emerald-400" : score >= 40 ? "bg-amber-400" : "bg-rose-400"
          }`} style={{ width:`${score}%` }} />
        </div>
      </div>
      {qs.reason && <p className="text-xs text-[#706E6B] mt-2">{qs.reason}</p>}
      {(qs.missing_fields || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {qs.missing_fields.map(f => (
            <span key={f} className="text-[10px] px-1.5 py-0.5 bg-rose-500/10 border border-rose-500/20 rounded text-rose-300 font-mono">{f}</span>
          ))}
        </div>
      )}
    </div>
  );

  /* BME: Journey Story left / CRM operational right */
  if (isBME) {
    return (
      <div className="p-4 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
          {/* Left: Journey Story */}
          <JourneyStoryPanel lead={lead} />

          {/* Right: CRM operational */}
          <div className="space-y-4">
            {/* Lead Ownership */}
            <div data-testid="enhanced-ownership-panel" className="bg-white border border-[#E5E5E5] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users2 className="w-4 h-4 text-[#706E6B]" />
                <h3 className="text-sm font-semibold text-[#181818]">Lead Ownership</h3>
              </div>
              <p className="text-[10px] text-[#706E6B] uppercase tracking-wider mb-1.5">Relationship Manager</p>
              {lead.owner_user_name ? (
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#EEF4FF] flex items-center justify-center">
                    <UserCheck className="w-3.5 h-3.5 text-[#0176D3]" />
                  </div>
                  <span className="text-sm font-medium text-[#181818]">{lead.owner_user_name}</span>
                </div>
              ) : (
                <p className="text-sm text-[#706E6B] italic mb-2">Not assigned</p>
              )}
              {lead.requires_owner_assignment && (
                <p className="text-[10px] text-amber-600 font-medium mb-2">Requires RM assignment</p>
              )}
              <div className="flex flex-col gap-1.5">
                {canAssignPoc && (
                  <button data-testid="assign-owner-btn" onClick={onAssignPoc}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg border border-[#0176D3]/30 text-[#0176D3] hover:bg-[#EEF4FF] transition-colors text-left">
                    {lead.owner_user_id ? "Change Relationship Manager" : "Assign Relationship Manager"}
                  </button>
                )}
                {canClaimLead && !lead.owner_user_id && !canAssignPoc && (
                  <button data-testid="claim-lead-btn" onClick={onClaimLead}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-[#0176D3]/40 bg-[#EEF4FF] text-[#0176D3] hover:bg-[#0176D3] hover:text-white transition-colors text-left">
                    Claim This Lead
                  </button>
                )}
                {!canAssignPoc && !canClaimLead && !lead.owner_user_id && (
                  <span className="text-xs text-[#a8a8a8] italic px-1">Not assigned</span>
                )}
              </div>
            </div>

            {/* EXP-01H: Customer Journey Timeline — readable business story */}
            <CompactJourneyTimeline leadId={leadId} />

            {/* AuditTrailPanel removed from primary visual path per PRASHASTA-CRM-UX-02.
                Audit trail is accessible from the Activity tab. */}

            <RecommendedActionsPanel lead={lead} currentUser={currentUser} onClaimLead={onClaimLead}
              onQualify={onQualify} onSendMessage={onSendMessage} onAssignPoc={onAssignPoc} onValidateIntent={onValidateIntent} />
            <QualCard />
            <Panel title="Lead Details" icon={ClipboardList}>
              <InfoPair label="Lead #"  value={lead.lead_number} mono />
              <InfoPair label="Status"  value={lead.status} mono />
              <InfoPair label="Intake"  value={lead.intake_state} mono />
              <InfoPair label="Created" value={fmtDate(lead.created_at)} />
              {lead.notes && <p className="text-xs text-[#706E6B] mt-2 leading-relaxed">{lead.notes}</p>}
            </Panel>
          </div>
        </div>
      </div>
    );
  }

  /* Non-BME: existing layout preserved */
  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <QualCard />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel title="Contact Information" icon={User} action={
          <button data-testid="edit-identity-btn" onClick={onEditIdentity}
            className="flex items-center gap-1 text-xs text-[#706E6B] hover:text-[#0176D3] transition-colors">
            <Pencil className="w-3 h-3" /> Edit
          </button>
        }>
          <InfoPair label="Name"     value={lead.customer_name || lead.contact_name} />
          <InfoPair label="Email"    value={lead.customer_contact_email || lead.customer_email || lead.contact_email} />
          <InfoPair label="Phone"    value={lead.customer_contact_phone || lead.contact_phone} />
          <InfoPair label="Source"   value={lead.source_channel} mono />
          <InfoPair label="Language" value={lead.preferred_language} />
        </Panel>
        <Panel title="Event Intent" icon={Target}>
          <InfoPair label="Type"     value={lead.event_type_interest || lead.event_type} />
          <InfoPair label="Date"     value={fmtDate(lead.tentative_event_date || lead.event_date)} />
          <InfoPair label="Location" value={lead.location || lead.event_location} />
          <InfoPair label="Guests"   value={lead.guest_count} />
          <InfoPair label="Budget"   value={lead.budget_range} />
          <InfoPair label="Venue"    value={lead.venue_status} mono />
        </Panel>
        <Panel title="Lead Ownership" icon={Users2}>
          <InfoPair label="Relationship Manager"
            value={lead.owner_user_name || (lead.owner_user_id ? "Assigned" : undefined)} />
          {lead.requires_owner_assignment && (
            <p className="text-[10px] text-[#E07B54] font-medium mt-0.5">Requires RM assignment</p>
          )}
          <InfoPair label="Rating" value={lead.rating} mono />
          <div className="pt-1 flex flex-col gap-1.5">
            {canAssignPoc && (
              <button data-testid="assign-owner-btn" onClick={onAssignPoc}
                className="text-xs text-[#0176D3] hover:underline text-left">
                {lead.owner_user_id ? "Change Relationship Manager" : "Assign Relationship Manager"}
              </button>
            )}
            {canClaimLead && !lead.owner_user_id && !canAssignPoc && (
              <button data-testid="claim-lead-btn" onClick={onClaimLead}
                className="text-xs font-semibold text-[#0176D3] hover:underline text-left">
                Claim This Lead
              </button>
            )}
            {!canAssignPoc && !canClaimLead && !lead.owner_user_id && (
              <span className="text-xs text-[#a8a8a8] italic">Not assigned</span>
            )}
          </div>
        </Panel>
        <Panel title="Lead Details" icon={ClipboardList}>
          <InfoPair label="Lead #"        value={lead.lead_number} mono />
          <InfoPair label="Status"        value={lead.status} mono />
          <InfoPair label="Source Detail" value={lead.source_detail} />
          <InfoPair label="Created"       value={fmtDate(lead.created_at)} />
          <InfoPair label="Updated"       value={fmtDate(lead.updated_at)} />
          {lead.notes && <p className="text-xs text-[#706E6B] mt-2 leading-relaxed">{lead.notes}</p>}
        </Panel>
      </div>
      {/* AuditTrailPanel removed from Overview tab per PRASHASTA-CRM-UX-02.
          Audit trail is accessible from the Activity tab. */}
      {/* Recommended Actions panel available for all leads */}
      <RecommendedActionsPanel lead={lead} currentUser={currentUser} onClaimLead={onClaimLead}
        onQualify={onQualify} onSendMessage={onSendMessage} onAssignPoc={onAssignPoc} onValidateIntent={onValidateIntent} />
    </div>
  );
}

// ─── Activity Tab ──────────────────────────────────────────────────────────────
function ActivityTab({ lead, leadId, users, currentUser, refreshKey = 0, onAddTask, onLogCall, onSendMessage, onScheduleFollowUp, onAddMeeting, onAddNote, onStatus, onEscalate, canChangeStatus, onActivityCreated, aiCenterRefreshKey = 0, onDispatchCall, onDispatchScheduleCall, onDispatchMeeting, onDispatchFollowUp, onDispatchNote, onDispatchMessage }) {
  const [tasks,      setTasks]      = useState(null);
  const [followUps,  setFollowUps]  = useState([]);
  const [messages,   setMessages]   = useState([]);
  const [loadingAct, setLoadingAct] = useState(true);

  const loadActivity = useCallback(async () => {
    setLoadingAct(true);
    try {
      const [taskRes, fuRes, msgRes] = await Promise.all([
        taskApi.list(leadId),
        followUpApi.list({ lead_id: leadId, limit: 50 }),
        conversationApi.getTimeline(leadId, { limit: 20 }),
      ]);
      setTasks(taskRes.data);
      setFollowUps(fuRes.data || []);
      setMessages(Array.isArray(msgRes.data) ? msgRes.data : (msgRes.data?.timeline || []));
    } catch { /* fail silently, tasks state remains null */ }
    finally { setLoadingAct(false); }
  }, [leadId]);

  useEffect(() => { loadActivity(); }, [loadActivity, refreshKey]);

  // Combine tasks + follow-ups into upcoming/overdue/recent
  const nowIso = new Date().toISOString();

  const upcomingTasks = tasks?.upcoming || [];
  const overdueTasks  = tasks?.overdue  || [];
  const recentTasks   = tasks?.recent_tasks || [];

  const upcomingFU = followUps.filter(f => f.status === "SCHEDULED" && f.scheduled_at >= nowIso);
  const overdueFU  = followUps.filter(f => f.status === "SCHEDULED" && f.scheduled_at < nowIso);
  const recentFU   = followUps.filter(f => f.status === "COMPLETED").slice(0, 5);

  const upcoming = [...upcomingTasks.map(t => ({ ...t, _type: "TASK" })),
                    ...upcomingFU.map(f => ({ ...f, _type: "FOLLOW_UP" }))].sort((a, b) =>
    (a.due_date || a.scheduled_at || "").localeCompare(b.due_date || b.scheduled_at || "")
  );
  const overdue = [...overdueTasks.map(t => ({ ...t, _type: "TASK" })),
                   ...overdueFU.map(f => ({ ...f, _type: "FOLLOW_UP" }))].sort((a, b) =>
    (a.due_date || a.scheduled_at || "").localeCompare(b.due_date || b.scheduled_at || "")
  );
  const recent = [...recentTasks.map(t => ({ ...t, _type: "TASK" })),
                  ...recentFU.map(f => ({ ...f, _type: "FOLLOW_UP" })),
                  ...messages.map(m => ({ ...m, _type: "MESSAGE" }))].sort((a, b) =>
    (b.completed_at || b.created_at || "").localeCompare(a.completed_at || a.created_at || "")
  ).slice(0, 12);

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      {/* CRM-AI-ACT-01 / CRM-AI-ACTION-02: AI-Assisted Action Center */}
      <AIActionCenter
        leadId={leadId}
        lead={lead}
        currentUser={currentUser}
        onTaskCreated={() => { loadActivity(); onActivityCreated?.(); }}
        externalRefreshKey={aiCenterRefreshKey}
        onDispatchCall={onDispatchCall}
        onDispatchScheduleCall={onDispatchScheduleCall}
        onDispatchMeeting={onDispatchMeeting}
        onDispatchFollowUp={onDispatchFollowUp}
        onDispatchNote={onDispatchNote}
        onDispatchMessage={onDispatchMessage}
      />

      {/* Journey Summary — factual, no fabricated timestamps */}
      <JourneySummarySection lead={lead} />

      {/* Quick action buttons */}
      <div className="grid grid-cols-3 gap-2">
        <QuickActionBtn icon={ClipboardList} label="Add Task"     testId={T.addTaskBtn}       onClick={onAddTask}           color="blue" />
        <QuickActionBtn icon={PhoneCall}     label="Log Call"     testId={T.logCallBtn}       onClick={onLogCall}           color="emerald" />
        <QuickActionBtn icon={Calendar}      label="Add Meeting"  testId={T.addMeetingBtn}    onClick={onAddMeeting}        color="sky" />
        <QuickActionBtn icon={Send}          label="Send Message" testId={T.sendMessageBtn}   onClick={onSendMessage}       color="purple" />
        <QuickActionBtn icon={Clock}         label="Follow-up"    testId={T.scheduleFollowUp} onClick={onScheduleFollowUp} color="amber" />
        <QuickActionBtn icon={FileText}      label="Add Note"     testId={T.addNoteBtn}       onClick={onAddNote}           color="slate" />
      </div>

      {/* Additional actions */}
      <div className="flex gap-2 flex-wrap">
        {canChangeStatus && (
          <button onClick={onStatus}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#444444] hover:border-slate-500 transition-colors">
            <ChevronDown className="w-3 h-3" /> Change Status
          </button>
        )}
        {canChangeStatus && (
          <button onClick={onEscalate}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E5E5E5] rounded text-[#706E6B] hover:text-rose-400 hover:border-rose-400/30 transition-colors">
            <AlertTriangle className="w-3 h-3" /> Escalate
          </button>
        )}
      </div>

      {loadingAct ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-12 bg-[#F3F3F3] rounded-lg animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Upcoming */}
          <ActivitySection
            title="Upcoming"
            testId={T.upcomingSection}
            items={upcoming}
            emptyMsg="No upcoming activities"
            onRefresh={loadActivity}
          />

          {/* Overdue */}
          <ActivitySection
            title="Overdue"
            testId={T.overdueSection}
            items={overdue}
            emptyMsg="No overdue activities"
            variant="overdue"
            onRefresh={loadActivity}
          />

          {/* Recent */}
          <ActivitySection
            title="Recent Activity"
            testId={T.recentSection}
            items={recent}
            emptyMsg="No recent activity"
            variant="recent"
            onRefresh={loadActivity}
          />
        </>
      )}
    </div>
  );
}

function QuickActionBtn({ icon: Icon, label, testId, onClick, color }) {
  const colorMap = {
    blue:    "border-blue-500/30 text-[#0176D3] hover:bg-blue-500/10",
    emerald: "border-emerald-500/30 text-[#2E844A] hover:bg-emerald-500/10",
    purple:  "border-[#D8CEF7] text-[#5E4DB2] hover:bg-[#F1ECFF]",
    amber:   "border-amber-500/30 text-[#8C4B00] hover:bg-amber-500/10",
    sky:     "border-sky-500/30 text-sky-600 hover:bg-sky-500/10",
    slate:   "border-slate-400/30 text-slate-600 hover:bg-slate-500/10",
  };
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 py-3 px-2 border rounded-xl text-xs font-medium transition-all ${colorMap[color] || colorMap.blue} bg-white`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function ActivitySection({ title, testId, items, emptyMsg, variant = "normal", onRefresh }) {
  const borderColor = variant === "overdue" ? "border-rose-500/30" : variant === "recent" ? "border-[#1C2740]" : "border-[#E5E5E5]";
  const titleColor  = variant === "overdue" ? "text-rose-400" : "text-[#444444]";

  return (
    <div data-testid={testId}>
      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${titleColor}`}>{title}</h4>
      {items.length === 0 ? (
        <p className="text-xs text-[#706E6B] py-2 pl-1">{emptyMsg}</p>
      ) : (
        <div className="space-y-1">
          {items.map((item, i) => (
            <ActivityItem key={item.id || i} item={item} variant={variant} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityItem({ item, variant, onRefresh }) {
  const [completing, setCompleting] = useState(false);

  const handleComplete = async () => {
    if (item._type !== "TASK") return;
    setCompleting(true);
    try {
      await taskApi.complete(item.id);
      toast.success("Task completed");
      onRefresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setCompleting(false); }
  };

  if (item._type === "MESSAGE") {
    // System / internal event (POC assignment, status change, etc.)
    if (item.direction === "INTERNAL" || item.channel === "SYSTEM") {
      const SystemIcon = item.activity_type === "LEAD_ASSIGNED" ? UserCheck
                       : item.activity_type === "LEAD_QUALIFIED" ? CheckCircle2
                       : item.activity_type === "LEAD_CONVERTED" ? TrendingUp
                       : Activity;
      return (
        <div className="flex items-center gap-2 py-1.5 px-3 rounded-lg"
          style={{ background: "#F4F6F9", border: "1px solid #E5E5E5" }}>
          <SystemIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#706E6B" }} />
          <p className="flex-1 text-xs" style={{ color: "#444444" }}>{item.content || "—"}</p>
          <p className="text-[10px] flex-shrink-0" style={{ color: "#9CA3AF" }}>{fmtRelative(item.created_at)}</p>
        </div>
      );
    }
    const isInbound = item.direction === "INBOUND";
    return (
      <div className="flex items-start gap-2 py-1.5 px-3 rounded-lg bg-white border border-[#1C2740]">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isInbound ? "bg-[#E5E5E5]" : "bg-[#EEF4FF]"}`}>
          {isInbound ? <User className="w-3 h-3 text-[#706E6B]" /> : <Bot className="w-3 h-3 text-[#0176D3]" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#706E6B] truncate">{item.content || "—"}</p>
          <p className="text-[10px] text-[#706E6B] mt-0.5">{fmtRelative(item.created_at)}</p>
        </div>
      </div>
    );
  }

  if (item._type === "FOLLOW_UP") {
    const isOverdue = variant === "overdue";
    return (
      <div className={`flex items-start gap-2 py-1.5 px-3 rounded-lg bg-white border ${isOverdue ? "border-rose-500/20" : "border-[#E5E5E5]"}`}>
        <Clock className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isOverdue ? "text-rose-400" : "text-[#8C4B00]"}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#444444]">{item.reason || item.follow_up_type}</p>
          <p className="text-[10px] text-[#706E6B] mt-0.5">
            {item.status === "COMPLETED" ? `Completed ${fmtRelative(item.completed_at)}` :
             `Scheduled ${fmtDate(item.scheduled_at)}`}
          </p>
        </div>
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
          item.status === "COMPLETED" ? "bg-emerald-500/10 text-[#2E844A]" :
          isOverdue ? "bg-rose-500/10 text-rose-400" : "bg-amber-500/10 text-[#8C4B00]"
        }`}>{item.status}</span>
      </div>
    );
  }

  // TASK — dispatch to specialized renderers by task_type
  const taskType = item.task_type || "TASK";

  if (taskType === "MEETING") {
    return (
      <div className={`flex items-start gap-2 py-1.5 px-3 rounded-lg bg-white border ${
        variant === "overdue" ? "border-rose-500/20" : "border-sky-500/20"
      }`}>
        <Calendar className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${variant === "overdue" ? "text-rose-400" : "text-sky-500"}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#444444] font-medium">{item.title || "Meeting"}</p>
          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#706E6B]">
            {item.due_date && <span>{fmtDate(item.due_date)}</span>}
            {item.notes    && <span className="truncate max-w-[200px]">{item.notes}</span>}
            {item.status === "COMPLETE" && <span className="text-[#2E844A]">Completed {fmtRelative(item.completed_at)}</span>}
          </div>
        </div>
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-600 border border-sky-500/20 flex-shrink-0">MEETING</span>
      </div>
    );
  }

  if (taskType === "NOTE") {
    return (
      <div className="flex items-start gap-2 py-1.5 px-3 rounded-lg bg-white border border-amber-500/20">
        <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
        <div className="flex-1 min-w-0">
          {item.title && <p className="text-xs text-[#444444] font-medium mb-0.5">{item.title}</p>}
          {item.notes && <p className="text-xs text-[#706E6B] leading-relaxed whitespace-pre-wrap break-words">{item.notes}</p>}
          <p className="text-[10px] text-[#706E6B] mt-0.5">{fmtRelative(item.created_at || item.completed_at)}</p>
        </div>
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20 flex-shrink-0">NOTE</span>
      </div>
    );
  }

  // TASK or CALL_LOG — original rendering
  const isComplete = item.status === "COMPLETE";
  return (
    <div className={`flex items-start gap-2 py-1.5 px-3 rounded-lg bg-white border ${
      variant === "overdue" ? "border-rose-500/20" : "border-[#E5E5E5]"
    }`}>
      {taskType === "CALL_LOG" ? (
        <PhoneCall className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#2E844A]" />
      ) : (
        <button onClick={handleComplete} disabled={isComplete || completing}
          className={`mt-0.5 flex-shrink-0 ${isComplete ? "text-[#2E844A] cursor-default" : "text-[#706E6B] hover:text-[#2E844A]"}`}>
          {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
           isComplete  ? <CheckSquare className="w-3.5 h-3.5" /> :
                         <Square className="w-3.5 h-3.5" />}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${isComplete ? "line-through text-[#706E6B]" : "text-[#444444]"}`}>
          {item.title}
          {taskType === "CALL_LOG" && item.call_outcome && (
            <span className="ml-2 text-[10px] font-mono text-[#706E6B]">{item.call_outcome}</span>
          )}
        </p>
        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#706E6B]">
          {item.due_date && <span>{fmtDate(item.due_date)}</span>}
          {item.notes    && <span className="truncate max-w-[160px]">{item.notes}</span>}
          {isComplete    && <span className="text-[#2E844A]">Completed {fmtRelative(item.completed_at)}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── GQP-04: Guest Estimate Panel ────────────────────────────────────────────
function GuestEstimatePanel({ lead, currentUser }) {
  const [estData, setEstData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const canView = hasCapability(currentUser, "event_estimate.read");

  useEffect(() => {
    if (!canView || !lead.estimate_ref) return;
    crmEstimateApi.getByRef(lead.estimate_ref)
      .then(r => setEstData(r.data))
      .catch(() => setError("Could not load estimate details."))
      .finally(() => setLoading(false));
  }, [lead.estimate_ref, canView]);

  if (!canView) return null;

  const fmtINR = (minor) => {
    if (!minor) return "—";
    return `₹${(minor / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div data-testid="guest-estimate-panel" className="border border-[#E7D8C0] rounded-xl overflow-hidden mt-4">
      {/* Header */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:opacity-90 transition-opacity"
        style={{
          background: "linear-gradient(to right, #FFFDF9, #FEF8EE)",
          borderBottom: expanded ? "1px solid #E7D8C0" : "none",
        }}
        data-testid="guest-estimate-toggle"
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4" style={{ color: "var(--brand-gold)" }} />
          <span className="text-sm font-semibold" style={{ color: "var(--brand-obsidian)" }}>
            Guest Estimate
          </span>
          {lead.estimate_ref && (
            <span className="text-[10px] px-2 py-0.5 rounded-full border font-mono"
              style={{ color: "#706E6B", borderColor: "#E7D8C0", background: "#FAFAF9" }}>
              {lead.estimate_ref}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {estData && (
            <span className="text-sm font-bold" style={{ color: "var(--brand-ruby)", fontFamily: "'Cormorant Garamond', serif" }}>
              {fmtINR(estData.total_priced_minor)}
            </span>
          )}
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-[#706E6B]" /> : <ChevronDown className="w-3.5 h-3.5 text-[#706E6B]" />}
        </div>
      </button>

      {expanded && (
        <div className="bg-white">
          {loading && (
            <div data-testid="guest-estimate-loading" className="flex items-center justify-center py-8 gap-2 text-[#706E6B]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs">Loading estimate…</span>
            </div>
          )}

          {error && (
            <div data-testid="guest-estimate-error" className="px-4 py-4 flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#706E6B]">{error}</p>
            </div>
          )}

          {estData && !loading && !error && (
            <>
              {/* Context bar */}
              <div data-testid="guest-estimate-context"
                className="px-4 py-2 border-b border-[#F3F3F3] bg-[#FAFAF9] flex flex-wrap gap-x-4 gap-y-1">
                {estData.guest_count > 0 && (
                  <span className="flex items-center gap-1 text-[11px] text-[#444]">
                    <Users className="w-3 h-3 text-[#706E6B]" /> {estData.guest_count} guests
                  </span>
                )}
                {estData.event_type_code && (
                  <span className="text-[11px] text-[#444]">{estData.event_type_code}</span>
                )}
                {estData.location_city && (
                  <span className="flex items-center gap-1 text-[11px] text-[#444]">
                    <MapPin className="w-3 h-3 text-[#706E6B]" /> {estData.location_city}
                  </span>
                )}
                {estData.event_date && (
                  <span className="flex items-center gap-1 text-[11px] text-[#444]">
                    <Calendar className="w-3 h-3 text-[#706E6B]" /> {fmtDate(estData.event_date)}
                  </span>
                )}
                <span className="text-[10px] text-[#B0AEAC] ml-auto">
                  Calculated {fmtDate(estData.issued_at)}
                  {estData.valid_until && ` · Valid until ${fmtDate(estData.valid_until)}`}
                </span>
              </div>

              {/* Incomplete pricing notice */}
              {estData.has_incomplete_pricing && (
                <div data-testid="estimate-incomplete-crm-notice"
                  className="mx-4 mt-3 px-3 py-2 rounded-lg flex gap-2 items-start"
                  style={{ background: "#FFFBEA", border: "1px solid #FFC107" }}>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-700">
                    This estimate covers only services with configured pricing.
                    {(estData.pricing_missing || []).length > 0 && ` Pricing missing for: ${estData.pricing_missing.join(", ")}.`}
                  </p>
                </div>
              )}

              {/* Service Breakdown Table */}
              <div className="px-4 pt-3 pb-2" data-testid="estimate-breakdown-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#F3F3F3]">
                        <th className="text-left text-[10px] font-semibold text-[#706E6B] uppercase tracking-wider py-1.5 pr-2">Service</th>
                        <th className="text-center text-[10px] font-semibold text-[#706E6B] uppercase tracking-wider py-1.5 pr-2">Qty</th>
                        <th className="text-right text-[10px] font-semibold text-[#706E6B] uppercase tracking-wider py-1.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(estData.service_breakdowns || []).map((bd, i) => (
                        <tr key={i} data-testid={`crm-estimate-line-${i}`}
                          className="border-b border-[#F9F9F9]">
                          <td className="py-2 pr-2">
                            <div className="font-medium text-[#3A3835]">{bd.service_name}</div>
                            {bd.pricing_model && (
                              <div className="text-[10px] text-[#B0AEAC]">{bd.pricing_model.replace(/_/g, " ")}</div>
                            )}
                          </td>
                          <td className="py-2 pr-2 text-center text-[#706E6B]">
                            {bd.quantity_effective || bd.quantity || 1}
                          </td>
                          <td className="py-2 text-right font-semibold text-[#3A3835]">
                            {fmtINR(bd.line_total_minor)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* POR items */}
              {(estData.price_on_request_items || []).length > 0 && (
                <div data-testid="crm-por-section"
                  className="mx-4 mb-2 px-3 py-2 rounded-lg bg-[#F9F9F9] border border-[#F3F3F3]">
                  <p className="text-[10px] font-semibold text-[#706E6B] uppercase tracking-wider mb-1.5">Price on Request</p>
                  {estData.price_on_request_items.map((item, i) => (
                    <div key={i} className="flex justify-between items-center py-0.5">
                      <span className="text-xs text-[#3A3835]">{item.service_name || item}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-white border border-[#E5E5E5] text-[#706E6B]">Price on Request</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Totals */}
              <div data-testid="estimate-totals-crm" className="mx-4 mb-3 rounded-lg overflow-hidden border border-[#F3F3F3]">
                <div className="bg-[#FAFAF9] px-3 py-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#706E6B]">Subtotal</span>
                    <span data-testid="crm-subtotal" className="text-[#3A3835]">{fmtINR(estData.subtotal_minor)}</span>
                  </div>
                  {estData.show_tax_line && estData.tax_total_minor > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-[#706E6B]">Taxes & Charges</span>
                      <span data-testid="crm-tax" className="text-[#3A3835]">{fmtINR(estData.tax_total_minor)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-1.5 border-t border-[#E5E5E5] mt-1">
                    <span className="text-sm font-semibold text-[#3A3835]">Estimated Total</span>
                    <span data-testid="crm-total" className="text-base font-bold"
                      style={{ color: "var(--brand-ruby)", fontFamily: "'Cormorant Garamond', serif" }}>
                      {fmtINR(estData.total_priced_minor)}
                    </span>
                  </div>
                  {estData.show_range && estData.range_minimum_minor && (
                    <div data-testid="crm-range" className="flex justify-between text-[11px] pt-1">
                      <span className="text-[#706E6B]">Indicative range</span>
                      <span className="text-[#3A3835]">
                        {fmtINR(estData.range_minimum_minor)} — {fmtINR(estData.range_maximum_minor)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rate card metadata */}
              <div className="px-4 pb-3 space-y-1.5">
                {estData.rate_card_code && (
                  <div className="flex items-center gap-1.5 text-[10px] text-[#B0AEAC]">
                    <BarChart2 className="w-3 h-3" />
                    Rate card: <strong className="text-[#706E6B]">{estData.rate_card_code}</strong>
                    {estData.rate_card_version && <span>(v{estData.rate_card_version})</span>}
                    {estData.rate_card_published_at && <span>· Published {fmtDate(estData.rate_card_published_at)}</span>}
                  </div>
                )}
                {estData.disclaimer_text && (
                  <div className="flex items-start gap-1.5 text-[10px] text-[#B0AEAC] leading-relaxed">
                    <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span>{estData.disclaimer_text}</span>
                  </div>
                )}
              </div>

              {/* RM Action */}
              <div data-testid="crm-initiate-quote-section"
                className="px-4 py-3 border-t border-[#F3F3F3] bg-[#FAFAF9] flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold text-[#3A3835]">Guest estimate — read-only snapshot</p>
                  <p className="text-[10px] text-[#706E6B]">
                    This is the guest's indicative estimate. Initiate a professional quote when ready.
                  </p>
                </div>
                <button
                  data-testid="initiate-professional-quote-btn"
                  disabled
                  title="Professional Quote workflow — available in the next release"
                  className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border opacity-50 cursor-not-allowed"
                  style={{ borderColor: "var(--brand-gold)", color: "var(--brand-gold)", background: "#FFFDF9" }}
                >
                  <FileBarChart2 className="w-3.5 h-3.5" />
                  Initiate Quote
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Event Intent Tab ──────────────────────────────────────────────────────────
function EventIntentTab({ lead, onEdit, onValidate, currentUser }) {
  const isBME = lead.lead_source === "BUILD_MY_EVENT";
  const ei    = lead.event_intent           || {};
  const prov  = lead.event_intent_provenance || {};
  const validation = lead.event_intent_validation || null;
  const canValidate = hasCapability(currentUser, "crm.lead.update");

  // Prefer event_intent sub-doc for BME leads, fall back to top-level fields
  const fields = [
    { label:"Event Type",      key:"event_type",        value: isBME ? (EVENT_TYPE_LABELS[ei.event_type] || ei.event_type) : (lead.event_type_interest || lead.event_type) },
    { label:"Occasion",                                  value: lead.occasion },
    { label:"Tentative Date",  key:"event_date",        value: fmtDate(isBME ? ei.event_date : (lead.tentative_event_date || lead.event_date)) },
    { label:"Location",        key:"event_city",        value: isBME ? (CITY_LABELS[ei.event_city] || ei.event_city) : (lead.location || lead.event_location) },
    { label:"Venue Status",    key:"venue_status",      value: isBME ? (VENUE_LABELS[ei.venue_status] || ei.venue_status) : lead.venue_status },
    { label:"Guest Count",     key:"guest_count_range", value: isBME ? (GUEST_LABELS[ei.guest_count_range] || ei.guest_count_range) : lead.guest_count },
    { label:"Exact Guest Count",                        value: isBME && (ei.guest_count_exact || lead.guest_count_exact) ? `${ei.guest_count_exact || lead.guest_count_exact} guests` : null },
    { label:"Budget Min",                               value: !isBME ? lead.budget_min : null },
    { label:"Budget Max",                               value: !isBME ? lead.budget_max : null },
    { label:"Budget Range",    key:"budget_range",      value: isBME ? (BUDGET_LABELS[ei.budget_range] || ei.budget_range) : lead.budget_range },
    { label:"Planning Scope",  key:"planning_scope",    value: isBME ? (ei.planning_scope === "END_TO_END" ? "End-to-End" : ei.planning_scope) : lead.planning_scope },
    { label:"Indoor / Outdoor",                         value: lead.indoor_outdoor },
    { label:"Theme / Style",                            value: lead.theme_style || lead.theme },
    { label:"Food Preferences",                         value: lead.food_preferences },
    { label:"Entertainment",                            value: lead.entertainment_needs },
    { label:"Description",                              value: lead.description || lead.inquiry_text, multiLine: true },
  ];
  const services   = (isBME ? ei.service_interests : null) || lead.service_interests || lead.services_interested_in || [];
  const eventFuncs = isBME ? (ei.event_functions || []) : [];
  const stylePref  = (isBME ? ei.style_preferences : null) || lead.style_preferences || [];
  const hasData    = fields.some(f => f.value) || services.length > 0;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <Panel title="Event Planning Details" icon={Target}
        action={onEdit && (
          <button data-testid="edit-event-intent-btn" onClick={onEdit}
            className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors hover:opacity-80"
            style={{ borderColor:"#E7D8C0", color:"var(--brand-gold)", background:"#FFFDF9" }}>
            <Pencil className="w-3 h-3" /> Edit
          </button>
        )}>
        {!hasData ? (
          <div className="py-8 text-center">
            <Target className="w-8 h-8 mx-auto mb-2 opacity-20" />
            <p className="text-sm text-[#706E6B]">No event details captured yet.</p>
            {onEdit && (
              <button data-testid="add-event-intent-btn" onClick={onEdit}
                className="mt-3 text-xs font-medium px-4 py-2 rounded-lg border transition-colors"
                style={{ borderColor:"var(--brand-gold)", color:"var(--brand-gold)" }}>
                + Add Event Details
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {fields.map(({ label, key, value, multiLine }) =>
              value ? (
                <div key={label} className={multiLine ? "col-span-2" : ""}>
                  {multiLine ? (
                    <div>
                      <span className="text-[10px] text-[#706E6B] uppercase tracking-wider">{label}</span>
                      <p className="text-xs text-[#444444] leading-relaxed mt-1">{value}</p>
                    </div>
                  ) : (
                    <div>
                      <InfoPair label={label} value={value} />
                      {isBME && key && prov[key] && (
                        <div className="flex items-center gap-1.5 mt-0.5 ml-0.5">
                          {prov[key].confirmed && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-[#2E844A] border border-emerald-500/20">Customer Selected</span>
                          )}
                          {!prov[key].confirmed && (
                            <span className="text-[10px] text-[#706E6B] italic">
                              {SOURCE_LABELS[prov[key].source] || prov[key].source}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : null
            )}
          </div>
        )}
        {services.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <span className="text-[10px] text-[#706E6B] uppercase tracking-wider block mb-1.5">Services Interested In</span>
            <div className="flex flex-wrap gap-1.5">
              {services.map(s => (
                <span key={s} className="text-[11px] px-2 py-0.5 bg-[#EEF4FF] border border-[#0176D3]/20 rounded text-[#706E6B]">
                  {SERVICE_LABELS[s] || s}
                </span>
              ))}
            </div>
          </div>
        )}
        {eventFuncs.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <span className="text-[10px] text-[#706E6B] uppercase tracking-wider block mb-1.5">Event Functions</span>
            <div className="flex flex-wrap gap-1.5">
              {eventFuncs.map(f => (
                <span key={f} className="text-[11px] px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 rounded text-rose-600">
                  {FUNCTION_LABELS[f] || f}
                </span>
              ))}
            </div>
          </div>
        )}
        {stylePref.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
            <span className="text-[10px] text-[#706E6B] uppercase tracking-wider block mb-1.5">Style Direction</span>
            <div className="flex flex-wrap gap-1.5">
              {stylePref.map(s => (
                <span key={s} className="text-[11px] px-2 py-0.5 bg-[#F1ECFF] border border-[#D8CEF7] rounded text-[#5E4DB2]">
                  {STYLE_LABELS[s] || s}
                </span>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {/* Source lineage */}
      {lead.source_detail && (
        <Panel title="Original Message" icon={MessageSquare}>
          <p className="text-xs text-[#706E6B] leading-relaxed whitespace-pre-wrap">{lead.source_detail}</p>
          {lead.original_language && lead.original_language !== "en" && (
            <p className="text-[10px] text-[#5E4DB2] mt-1">Language detected: {lead.original_language.toUpperCase()}</p>
          )}
        </Panel>
      )}

      {/* ── Intent Validation Panel ────────────────────────────────────────── */}
      <Panel
        title="Intent Validation"
        icon={CheckCircle2}
        action={canValidate && onValidate && (
          <button
            data-testid="validate-intent-btn"
            onClick={onValidate}
            className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors hover:opacity-80"
            style={{ borderColor:"#E7D8C0", color:"var(--brand-gold)", background:"#FFFDF9" }}
          >
            <CheckCircle2 className="w-3 h-3" />
            {validation ? "Update Validation" : "Validate Intent"}
          </button>
        )}
      >
        {!validation ? (
          <div className="py-4 text-center" data-testid="intent-validation-empty">
            <CheckCircle2 className="w-7 h-7 mx-auto mb-2 opacity-20" />
            <p className="text-sm text-[#706E6B]">No validation recorded yet.</p>
            {canValidate && onValidate && (
              <button
                data-testid="validate-intent-inline-btn"
                onClick={onValidate}
                className="mt-2 text-xs font-medium underline"
                style={{ color:"var(--brand-gold)" }}
              >
                Record intent validation
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="intent-validation-record">
            <div className="flex items-center gap-2">
              {validation.status === "VALIDATED" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="w-3 h-3" /> Validated
                </span>
              )}
              {validation.status === "CLARIFICATION_REQUIRED" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                  <AlertTriangle className="w-3 h-3" /> Clarification Required
                </span>
              )}
              {validation.status === "NOT_PROCEEDING" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                  <X className="w-3 h-3" /> Not Proceeding
                </span>
              )}
            </div>
            {validation.notes && (
              <p className="text-xs text-[#3A3835] leading-relaxed bg-[#FAFAF9] rounded-md px-3 py-2 border border-[#F3F3F3]">
                {validation.notes}
              </p>
            )}
            <p className="text-[10px] text-[#B0AEAC]">
              Reviewed by {validation.validated_by_email || validation.validated_by}
              {validation.validated_at && ` on ${new Date(validation.validated_at).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}`}
            </p>
          </div>
        )}
      </Panel>

      {/* Raw intent payload — under Technical Details, capability-gated */}
      {isBME && <RawIntentDetails lead={lead} />}

      {/* GQP-04: Guest Estimate Panel — only for BME leads with an estimate */}
      {isBME && lead.estimate_ref && <GuestEstimatePanel lead={lead} currentUser={currentUser} />}
    </div>
  );
}

// ─── Related Tab ───────────────────────────────────────────────────────────────
function RelatedTab({ lead, leadId }) {
  const navigate = useNavigate();
  const [related,  setRelated]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    relatedApi.getLeadRelated(leadId)
      .then(r => setRelated(r.data))
      .catch(() => setRelated({ account: null, contact: null, opportunity: null, event: null, duplicate_candidates: [] }))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return (
    <div className="p-4 space-y-3">
      {[1,2,3,4].map(i => <div key={i} className="h-16 bg-[#F3F3F3] rounded-xl animate-pulse" />)}
    </div>
  );

  const dups = related?.duplicate_candidates || [];

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-3">

      {/* Account */}
      <RelatedCard
        testId={T.relatedAccount}
        title="Account"
        icon={User}
        record={related?.account}
        emptyMsg="Account not linked"
        emptyNote="Will be created or resolved when Event Planning starts"
        onOpen={related?.account ? () => {} : null}
      >
        {related?.account && (
          <div className="space-y-1">
            <InfoPair label="Name"   value={related.account.name} />
            <InfoPair label="Type"   value={related.account.type} mono />
            <InfoPair label="Status" value={related.account.status} mono />
            <InfoPair label="Email"  value={related.account.email} />
          </div>
        )}
      </RelatedCard>

      {/* Contact */}
      <RelatedCard
        testId={T.relatedContact}
        title="Contact"
        icon={UserPlus}
        record={related?.contact}
        emptyMsg="Contact not linked"
        emptyNote="Will be created or resolved when Event Planning starts"
        onOpen={related?.contact ? () => {} : null}
      >
        {related?.contact && (
          <div className="space-y-1">
            <InfoPair label="Name"  value={related.contact.name} />
            <InfoPair label="Email" value={related.contact.email} />
            <InfoPair label="Phone" value={related.contact.phone} />
            <InfoPair label="Role"  value={related.contact.role} mono />
          </div>
        )}
      </RelatedCard>

      {/* Opportunity */}
      <RelatedCard
        testId={T.relatedOpp}
        title="Opportunity"
        icon={Star}
        record={related?.opportunity}
        emptyMsg="Opportunity not created"
        emptyNote="Opportunity will be created when Event Planning starts"
        onOpen={null}
      >
        {related?.opportunity && (
          <div className="space-y-1">
            <InfoPair label="Opp #"   value={related.opportunity.opportunity_number} mono />
            <InfoPair label="Stage"   value={related.opportunity.stage} mono />
            {related.opportunity.value_inr && (
              <InfoPair label="Value" value={`₹${related.opportunity.value_inr.toLocaleString()}`} />
            )}
            {related.opportunity.contact_name && (
              <InfoPair label="Contact" value={related.opportunity.contact_name} />
            )}
            {/* UEIM-01: contextual link to Moodboard Designer */}
            {(related.opportunity.id || related.opportunity._id) && (
              <button
                onClick={() => navigate(`/crm/opportunities/${related.opportunity.id || related.opportunity._id}`)}
                className="mt-2 flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg font-medium w-full justify-center"
                style={{ background: "rgba(201,161,95,0.12)", color: "#C9A15F", border: "1px solid rgba(201,161,95,0.30)" }}
                data-testid="open-event-design-btn"
              >
                Open Event Design
              </button>
            )}
          </div>
        )}
      </RelatedCard>

      {/* Event */}
      <RelatedCard
        testId={T.relatedEvent}
        title="Event"
        icon={Calendar}
        record={related?.event}
        emptyMsg="No event created"
        emptyNote={lead.linked_event_id ? "Loading event…" : "Start Event Planning to create an Event"}
        onOpen={related?.event ? () => navigate(`/imagine/events/${related.event.id}`) : null}
      >
        {related?.event && (
          <div className="space-y-1">
            <InfoPair label="Event #"  value={related.event.event_number} mono />
            <InfoPair label="Type"     value={related.event.event_type} />
            <InfoPair label="Occasion" value={related.event.occasion} />
            <InfoPair label="Status"   value={related.event.status} mono />
          </div>
        )}
      </RelatedCard>

      {/* Duplicate check */}
      <div data-testid={T.duplicateStatus}
        className="bg-white border border-[#E5E5E5] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-[#706E6B]" />
          <h4 className="text-sm font-semibold text-[#181818] font-sans">Duplicate Check</h4>
        </div>
        {dups.length === 0 ? (
          <div className="flex items-center gap-2 text-[#2E844A] text-sm">
            <CheckCircle2 className="w-4 h-4" />
            No potential duplicates found
          </div>
        ) : (
          <div className="space-y-2">
            {dups.map(d => (
              <div key={d.id} className="flex items-start gap-3 p-2 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5 text-[#8C4B00] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#8C4B00]">Possible existing customer found</p>
                  <p className="text-xs text-[#706E6B] mt-0.5">{d.name}</p>
                  <p className="text-[10px] text-[#706E6B]">{d.email}</p>
                </div>
                <button className="text-[10px] px-2 py-0.5 border border-amber-500/30 rounded text-[#8C4B00] hover:bg-amber-500/10">
                  Review
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RelatedCard({ testId, title, icon: Icon, record, emptyMsg, emptyNote, onOpen, children }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#E5E5E5] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#706E6B]" />
          <h4 className="text-sm font-semibold text-[#181818] font-sans">{title}</h4>
        </div>
        {record && onOpen && (
          <button onClick={onOpen}
            className="text-xs text-[#0176D3] hover:underline flex items-center gap-1">
            Open <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
          </button>
        )}
      </div>
      {record ? children : (
        <div className="space-y-0.5">
          <p className="text-xs text-[#706E6B]">{emptyMsg}</p>
          {emptyNote && <p className="text-[10px] text-[#706E6B]">{emptyNote}</p>}
        </div>
      )}
    </div>
  );
}

// ─── AI Insights Tab ───────────────────────────────────────────────────────────
function AIInsightsTab({ lead, qs }) {
  const [policyStatus, setPolicyStatus] = useState(null);

  useEffect(() => {
    agentApi.getPolicyStatus(lead.id).then(r => setPolicyStatus(r.data)).catch(() => {});
  }, [lead.id]);

  const policy = policyStatus?.policy;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      {/* AI Summary */}
      {lead.ai_summary && (
        <Panel title="AI Summary" icon={Bot}>
          <p className="text-sm text-[#444444] leading-relaxed">{lead.ai_summary}</p>
        </Panel>
      )}

      {/* Qualification Explanation */}
      <Panel title="Qualification Analysis" icon={Zap}>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-[#F3F3F3] rounded-lg p-2.5">
            <p className="text-[10px] text-[#706E6B] uppercase tracking-wider">Score</p>
            <p className="text-xl font-bold text-[#181818] mt-0.5">{qs.score ?? 0}<span className="text-sm text-[#706E6B]">%</span></p>
          </div>
          <div className="bg-[#F3F3F3] rounded-lg p-2.5">
            <p className="text-[10px] text-[#706E6B] uppercase tracking-wider">Status</p>
            <p className="text-sm font-semibold text-[#181818] mt-0.5">{qs.status || "—"}</p>
          </div>
        </div>
        {qs.reason && <p className="text-xs text-[#706E6B] leading-relaxed">{qs.reason}</p>}

        {(qs.known_fields || []).length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] text-[#2E844A] uppercase tracking-wider mb-1.5">Known</p>
            <div className="flex flex-wrap gap-1">
              {qs.known_fields.map(f => (
                <span key={f} className="text-[10px] px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-[#2E844A] font-mono">{f}</span>
              ))}
            </div>
          </div>
        )}
        {(qs.missing_fields || []).length > 0 && (
          <div className="mt-2">
            <p className="text-[10px] text-rose-400 uppercase tracking-wider mb-1.5">Missing</p>
            <div className="flex flex-wrap gap-1">
              {qs.missing_fields.map(f => (
                <span key={f} className="text-[10px] px-1.5 py-0.5 bg-rose-500/10 border border-rose-500/20 rounded text-rose-300 font-mono">{f}</span>
              ))}
            </div>
          </div>
        )}
      </Panel>

      {/* Agent Policy */}
      {policy && (
        <Panel title="Agent Policy" icon={Shield}>
          <InfoPair label="Policy"    value={policy.policy_id} mono />
          <InfoPair label="Autonomy"  value={policy.autonomy_level} mono />
          <InfoPair label="Decision"  value={policy.result} mono />
          {policy.reason && <p className="text-xs text-[#706E6B] mt-1">{policy.reason}</p>}
        </Panel>
      )}

      {/* Lead Intelligence */}
      {lead.extracted_requirements && (
        <Panel title="Extracted Intelligence" icon={Info}>
          {Object.entries(lead.extracted_requirements).map(([k, v]) =>
            v ? <InfoPair key={k} label={k.replace(/_/g, " ")} value={typeof v === "object" ? JSON.stringify(v) : String(v)} /> : null
          )}
        </Panel>
      )}
    </div>
  );
}

// ─── Modals ────────────────────────────────────────────────────────────────────

function AddTaskModal({ leadId, users, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", due_date: "", assignee_id: "", notes: "", task_type: "TASK" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setLoading(true);
    try {
      await taskApi.create(leadId, {
        ...form,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      });
      toast.success("Task created");
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Add Task" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Title *">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            data-testid="add-task-title"
            placeholder="Task description..." className={inputCls} />
        </Field>
        <Field label="Due Date">
          <input type="datetime-local" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
            data-testid="add-task-due-date" className={inputCls} />
        </Field>
        <Field label="Assignee">
          <select value={form.assignee_id} onChange={e => setForm(f => ({ ...f, assignee_id: e.target.value }))}
            className={inputCls}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </Field>
        <Field label="Notes">
          <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Optional notes..." className={inputCls} />
        </Field>
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Create Task" />
      </div>
    </Modal>
  );
}

function LogCallModal({ leadId, onClose, onDone, prefill }) {
  const [form, setForm] = useState({
    title:       prefill?.title  || "",
    call_outcome: "CONNECTED",
    notes:       prefill?.notes  || "",
    duration_mins: "",
    task_type:   "CALL_LOG",
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Call summary required"); return; }
    setLoading(true);
    try {
      await taskApi.create(leadId, {
        ...form,
        duration_mins: form.duration_mins ? Number(form.duration_mins) : null,
        due_date: null,
      });
      toast.success("Call logged");
      onDone();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Log Call" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Call Summary *">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            data-testid="log-call-title"
            placeholder="e.g. Called to confirm venue availability" className={inputCls} />
        </Field>
        <Field label="Outcome">
          <select value={form.call_outcome} onChange={e => setForm(f => ({ ...f, call_outcome: e.target.value }))}
            data-testid="log-call-outcome" className={inputCls}>
            {["CONNECTED","VOICEMAIL","NO_ANSWER","BUSY"].map(o => (
              <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
            ))}
          </select>
        </Field>
        <Field label="Duration (mins)">
          <input type="number" value={form.duration_mins} onChange={e => setForm(f => ({ ...f, duration_mins: e.target.value }))}
            placeholder="Optional" className={inputCls} />
        </Field>
        <Field label="Notes">
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} placeholder="Call notes..." className={`${inputCls} resize-none`} />
        </Field>
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Log Call" />
      </div>
    </Modal>
  );
}

function SendMessageModal({ lead, leadId, onClose, onDone }) {
  const [channel, setChannel]         = useState("WHATSAPP");
  const [tone, setTone]               = useState("PROFESSIONAL");
  const [draft, setDraft]             = useState("");
  const [subject, setSubject]         = useState("");
  const [mode, setMode]               = useState(null);   // null | "ai-draft" | "manual"
  const [draftLoading, setDraftLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [contextNotes, setContextNotes] = useState("");
  const [showContext, setShowContext]  = useState(false);
  const [composerStatus, setComposerStatus] = useState(null);
  const [statusLoading, setStatusLoading]   = useState(true);
  const [sendResult, setSendResult]   = useState(null);   // result after confirmed send
  // Stable idempotency key per modal open — survives re-renders / retry clicks
  const idempotencyKey = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `gate3-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  // ── Load channel availability on mount ────────────────────────────────────
  useEffect(() => {
    crmComposerApi.getStatus(leadId)
      .then(r => setComposerStatus(r.data))
      .catch(() => {})
      .finally(() => setStatusLoading(false));
  }, [leadId]);

  // ── Derive channel-level info ──────────────────────────────────────────────
  const chanInfo   = composerStatus?.[channel.toLowerCase()] || null;
  const canSend    = chanInfo ? chanInfo.can_send : true;   // default permissive before load
  const isMock     = chanInfo?.provider_mode === "MOCK";
  const recipient  = chanInfo?.phone || chanInfo?.email || "";
  const tNotice    = chanInfo?.transport_notice || "";

  // ── AI Draft ──────────────────────────────────────────────────────────────
  const handleAIDraft = async () => {
    setDraftLoading(true);
    try {
      const { data } = await recommendationApi.draftMessage(leadId, channel, tone, contextNotes);
      setDraft(data.draft_text || "");
      setSubject(data.draft_subject || "");
      setMode("ai-draft");
      toast.success("AI draft ready — review before sending");
    } catch {
      try {
        const { data } = await agentApi.draftReply({ lead_id: leadId, language: "en" });
        setDraft(data.draft || "");
        setMode("ai-draft");
        toast.success("Draft ready — please review");
      } catch (e) { toast.error(formatError(e)); }
    } finally { setDraftLoading(false); }
  };

  // ── Real dispatch via canonical CRM Composer ──────────────────────────────
  const handleSend = async () => {
    if (!draft.trim()) { toast.error("Message content required"); return; }
    if (channel === "EMAIL" && !subject.trim()) { toast.error("Email subject is required"); return; }
    setSendLoading(true);
    try {
      let result;
      if (channel === "WHATSAPP") {
        const { data } = await crmComposerApi.sendWhatsApp(leadId, {
          content: draft.trim(),
          idempotency_key: idempotencyKey.current,
          consent_acknowledged: true,
        });
        result = data;
      } else {
        const { data } = await crmComposerApi.sendEmail(leadId, {
          subject: subject.trim(),
          content: draft.trim(),
          idempotency_key: idempotencyKey.current,
        });
        result = data;
      }
      setSendResult(result);
      onDone(); // refresh activity timeline
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSendLoading(false);
    }
  };

  const CHANNELS = [
    { value: "WHATSAPP", label: "WhatsApp" },
    { value: "EMAIL",    label: "Email" },
  ];
  const TONES = [
    { value: "PROFESSIONAL", label: "Professional" },
    { value: "WARM",         label: "Warm & Friendly" },
    { value: "FORMAL",       label: "Formal" },
  ];

  // ── Post-send confirmation screen ─────────────────────────────────────────
  if (sendResult) {
    const isSimulated = sendResult.transport === "SIMULATED";
    return (
      <Modal title="Message Dispatched" onClose={onClose}>
        <div className="space-y-3" data-testid="send-result-panel">
          {isSimulated ? (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="simulated-badge">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-700">SIMULATED — Mock Transport</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  This message was routed to the mock outbox. No real message was delivered.
                  Activate a live provider in Communications Admin to send real messages.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <p className="text-xs font-semibold text-emerald-700">Message sent via live provider</p>
            </div>
          )}
          <div className="text-xs text-[#706E6B] space-y-1 px-1">
            <div className="flex justify-between">
              <span className="text-[#A8A8A8]">Channel</span>
              <span className="font-medium text-[#181818]">{sendResult.channel}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#A8A8A8]">Provider</span>
              <span className="font-medium text-[#181818]">{sendResult.provider}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#A8A8A8]">Transport</span>
              <span className={`font-semibold ${isSimulated ? "text-amber-600" : "text-emerald-600"}`}>
                {sendResult.transport}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#A8A8A8]">Recipient</span>
              <span className="font-medium text-[#181818]">{sendResult.recipient}</span>
            </div>
            {sendResult.idempotent_replay && (
              <p className="text-[10px] text-[#A8A8A8] pt-1">
                Idempotent replay — same message was already dispatched.
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="send-result-close-btn"
            className="w-full py-2 bg-[#181818] text-white text-sm rounded-lg hover:bg-[#333] transition-colors">
            Done
          </button>
        </div>
      </Modal>
    );
  }

  // ── Compose screen ─────────────────────────────────────────────────────────
  return (
    <Modal title="Send Message" onClose={onClose}>
      <div className="space-y-3">
        {/* Provider badge + channel status */}
        {!statusLoading && chanInfo && (
          <div className="flex items-center justify-between px-2.5 py-1.5 bg-[#F8F8F8] border border-[#E5E5E5] rounded-lg text-xs">
            <div className="flex items-center gap-1.5 text-[#706E6B]">
              {isMock ? <AlertTriangle className="w-3 h-3 text-amber-500" /> : <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
              <span data-testid="provider-mode-badge"
                className={isMock ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                {isMock ? "MOCK" : "LIVE"}
              </span>
              {tNotice && <span className="text-[#A8A8A8]">· {tNotice}</span>}
            </div>
            {recipient && (
              <span className="text-[#A8A8A8]" data-testid="composer-recipient">
                {recipient}
              </span>
            )}
          </div>
        )}

        {!mode ? (
          <>
            {/* Channel + Tone selectors */}
            <div className="flex gap-2">
              <Field label="Channel">
                <select data-testid="channel-selector" value={channel}
                  onChange={e => { setChannel(e.target.value); setDraft(""); setSubject(""); }}
                  className={inputCls}>
                  {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Tone">
                <select value={tone} onChange={e => setTone(e.target.value)} className={inputCls}>
                  {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>

            {/* Channel unavailable warning */}
            {!statusLoading && chanInfo && !canSend && (
              <div className="flex items-center gap-2 px-2.5 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-600" data-testid="channel-unavailable-notice">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                {channel === "WHATSAPP"
                  ? "No phone number on this lead — cannot send WhatsApp."
                  : "No email address on this lead — cannot send email."}
              </div>
            )}

            {/* Optional context for AI */}
            <button onClick={() => setShowContext(c => !c)} className="text-xs text-[#0176D3] hover:underline flex items-center gap-1">
              <ChevronDown className="w-3 h-3" /> Add context for AI
            </button>
            {showContext && (
              <textarea value={contextNotes} onChange={e => setContextNotes(e.target.value)} rows={2}
                placeholder="e.g. customer asked about venue options on last call…"
                className={`${inputCls} resize-none text-xs`} />
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={handleAIDraft} disabled={draftLoading} data-testid="ai-draft-btn"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-[#C9A15F]/30 rounded text-sm text-[#C9A15F] hover:bg-[#C9A15F]/5 disabled:opacity-60">
                {draftLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {draftLoading ? "Drafting…" : "Draft with AI"}
              </button>
              <button onClick={() => setMode("manual")} data-testid="manual-message-btn"
                className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-[#E5E5E5] rounded text-sm text-[#706E6B] hover:text-[#181818]">
                <MessageSquare className="w-4 h-4" /> Write Manually
              </button>
            </div>
          </>
        ) : (
          <>
            {/* AI badge */}
            {mode === "ai-draft" && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#C9A15F]/5 border border-[#C9A15F]/20 rounded text-xs text-[#706E6B]">
                <Sparkles className="w-3 h-3 text-[#C9A15F] flex-shrink-0" />
                AI draft — review and edit before sending. AI output may require corrections.
              </div>
            )}
            {channel === "EMAIL" && (
              <Field label="Subject *">
                <input value={subject} onChange={e => setSubject(e.target.value)} className={inputCls}
                  placeholder="Email subject…" data-testid="msg-subject" />
              </Field>
            )}
            <Field label="Message *">
              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={5}
                placeholder="Write your message…" className={`${inputCls} resize-none`} data-testid="msg-body" />
            </Field>
            <div className="flex gap-2">
              <button onClick={() => { setMode(null); setDraft(""); setSubject(""); }}
                className="text-xs text-[#706E6B] hover:underline flex items-center gap-1">
                ← Back
              </button>
              {mode === "ai-draft" && (
                <button onClick={handleAIDraft} disabled={draftLoading}
                  className="text-xs text-[#C9A15F] hover:underline flex items-center gap-1 ml-auto disabled:opacity-60">
                  {draftLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Regenerate
                </button>
              )}
            </div>
            {/* Gate 3 contract: button disabled when channel has no recipient */}
            <ModalActions
              onClose={() => { setMode(null); setDraft(""); onClose(); }}
              onSubmit={handleSend}
              loading={sendLoading}
              disabled={sendLoading || !canSend}
              label={`Send via ${channel}${isMock ? " (Simulated)" : ""}`}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

function FollowUpModal({ leadId, users, onClose, onDone, prefill }) {
  const [form, setForm] = useState({
    follow_up_type: prefill?.follow_up_type || "GENERAL",
    reason:         prefill?.reason         || prefill?.title || "",
    scheduled_at:   prefill?.due_date       || "",
    owner_id:       "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.scheduled_at || !form.reason) { toast.error("All fields required"); return; }
    setLoading(true);
    try {
      await followUpApi.create({ lead_id: leadId, ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success("Follow-up scheduled");
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Schedule Follow-up" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Type">
          <select value={form.follow_up_type} onChange={e => setForm(f => ({ ...f, follow_up_type: e.target.value }))} className={inputCls}>
            {["WAITING_FOR_CUSTOMER","PROPOSAL_SENT","MEETING_REQUESTED","CUSTOMER_INACTIVE","POC_ACTION_REQUIRED","GENERAL"].map(t =>
              <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
            )}
          </select>
        </Field>
        <Field label="Reason *">
          <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
            placeholder="Follow-up reason..." className={inputCls} />
        </Field>
        <Field label="Scheduled At *">
          <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
            className={inputCls} />
        </Field>
        <Field label="Assignee">
          <select value={form.owner_id} onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))} className={inputCls}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Schedule" />
      </div>
    </Modal>
  );
}

function LeadIdentityModal({ lead, onClose, onUpdated }) {
  const original = {
    name:  lead.customer_name || lead.contact_name || "",
    email: lead.customer_contact_email || lead.customer_email || lead.contact_email || "",
    phone: lead.customer_contact_phone || lead.contact_phone || "",
  };
  const [form, setForm]     = useState({ ...original });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: "" })); };

  const submit = async () => {
    const patch = {};
    if (form.name  !== original.name)  patch.customer_name          = form.name;
    if (form.email !== original.email) patch.customer_contact_email = form.email;
    if (form.phone !== original.phone) patch.customer_contact_phone = form.phone;

    if (Object.keys(patch).length === 0) { toast.info("No changes to save"); onClose(); return; }
    if (!form.name.trim()) { setErrors({ name: "Name is required" }); return; }

    setSaving(true);
    try {
      await leadApi.update(lead.id, patch);
      toast.success("Lead identity updated");
      onUpdated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") toast.error(detail);
      else toast.error(formatError(e));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Edit Contact Identity" onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-[#444] mb-1">Customer Name <span className="text-rose-500">*</span></label>
          <input
            data-testid="edit-identity-name"
            value={form.name}
            onChange={e => set("name", e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]/30 ${errors.name ? "border-rose-400" : "border-[#E5E5E5]"}`}
          />
          {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-[#444] mb-1">Email</label>
          <input
            data-testid="edit-identity-email"
            type="email"
            value={form.email}
            onChange={e => set("email", e.target.value)}
            className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]/30"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#444] mb-1">Phone</label>
          <input
            data-testid="edit-identity-phone"
            value={form.phone}
            onChange={e => set("phone", e.target.value)}
            className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]/30"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B]">Cancel</button>
        <button
          data-testid="edit-identity-submit-btn"
          onClick={submit}
          disabled={saving}
          className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
}


// ─── Validate Intent Modal ───────────────────────────────────────────────────
const INTENT_CHECKLIST = [
  { id: "event_type",    label: "Event type and intended occasion confirmed" },
  { id: "event_date",    label: "Event date confirmed" },
  { id: "location",      label: "Location and venue expectations confirmed" },
  { id: "guest_count",   label: "Guest count confirmed" },
  { id: "budget",        label: "Budget expectations confirmed" },
  { id: "services",      label: "Requested services confirmed" },
  { id: "contact",       label: "Customer contact details confirmed" },
];

function ValidateIntentModal({ lead, leadId, currentUser, onClose, onValidated }) {
  const [status,             setStatus]             = useState("VALIDATED");
  const [notes,              setNotes]              = useState("");
  const [checklist,          setChecklist]          = useState(
    Object.fromEntries(INTENT_CHECKLIST.map(c => [c.id, false]))
  );
  const [submitting,         setSubmitting]         = useState(false);
  // Governance: explicit reviewer acknowledgement required before recording VALIDATED
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);

  const allChecked      = INTENT_CHECKLIST.every(c => checklist[c.id]);
  const checkedCount    = Object.values(checklist).filter(Boolean).length;
  const isIndeterminate = checkedCount > 0 && !allChecked;

  const notesRequired  = status === "CLARIFICATION_REQUIRED" || status === "NOT_PROCEEDING";
  const notesValid     = !notesRequired || notes.trim().length > 0;
  // VALIDATED requires all items checked AND explicit reviewer acknowledgement
  const validatedReady = status === "VALIDATED" ? (allChecked && reviewAcknowledged) : true;
  const canSubmit      = notesValid && validatedReady && !submitting;

  // Any change to checklist resets the acknowledgement to enforce fresh sign-off
  const toggleCheck = (id) => {
    setChecklist(p => ({ ...p, [id]: !p[id] }));
    setReviewAcknowledged(false);
  };

  const selectAll = () => {
    setChecklist(Object.fromEntries(INTENT_CHECKLIST.map(c => [c.id, true])));
    setReviewAcknowledged(false); // bulk action always requires explicit re-acknowledgement
  };

  const clearAll = () => {
    setChecklist(Object.fromEntries(INTENT_CHECKLIST.map(c => [c.id, false])));
    setReviewAcknowledged(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await recommendationApi.validateIntent(leadId, {
        validation_status: status,
        notes: notes.trim() || undefined,
        checklist_snapshot: checklist,
      });
      const labels = { VALIDATED: "validated", CLARIFICATION_REQUIRED: "clarification required", NOT_PROCEEDING: "marked not proceeding" };
      toast.success(`Event intent ${labels[status]}`);
      onValidated?.();
    } catch (err) {
      const status_code = err?.response?.status;
      if (status_code === 403) {
        toast.error("You do not have permission to validate event intent.");
      } else if (status_code === 422) {
        toast.error(err?.response?.data?.detail || "Validation failed — check required fields.");
      } else {
        toast.error("Could not save validation. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const ei     = lead.event_intent || {};
  const evType = ei.event_type || lead.event_type_interest;
  const evDate = ei.event_date  || lead.tentative_event_date;
  const loc    = ei.venue_city  || ei.location_preference || lead.location;
  const guests = ei.guest_count_range || lead.guest_count;
  const budget = ei.budget_range || lead.budget_range;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" data-testid="validate-intent-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F3F3F3]">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" style={{ color:"var(--brand-gold)" }} />
            <span className="font-semibold text-[#1A1816] text-base">Validate Event Intent</span>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F3F3F3]" data-testid="validate-intent-close-btn">
            <X className="w-4 h-4 text-[#706E6B]" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {/* Submitted details summary */}
          <div className="bg-[#FAFAF9] rounded-xl p-3 border border-[#F3F3F3] space-y-1.5">
            <p className="text-[11px] font-semibold text-[#B0AEAC] uppercase tracking-wider">Customer's Submitted Requirements</p>
            {[
              ["Event Type",  evType],
              ["Event Date",  evDate ? new Date(evDate).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" }) : null],
              ["Location",    loc],
              ["Guests",      guests],
              ["Budget",      budget],
            ].map(([l, v]) => v && (
              <div key={l} className="flex gap-2 text-xs">
                <span className="text-[#B0AEAC] w-20 flex-shrink-0">{l}</span>
                <span className="text-[#3A3835] font-medium">{v}</span>
              </div>
            ))}
          </div>

          {/* Verification Checklist */}
          <div>
            {/* ── Bulk controls header ─────────────────────────────────── */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-[#3A3835]">Verification Checklist</p>
                {/* X of Y counter badge */}
                <span
                  data-testid="checklist-counter-badge"
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors"
                  style={{
                    background: allChecked  ? "#DCFCE7" :
                                isIndeterminate ? "#FEF3C7" : "#F3F3F3",
                    color:      allChecked  ? "#15803D" :
                                isIndeterminate ? "#92400E" : "#706E6B",
                  }}
                >
                  {checkedCount} of {INTENT_CHECKLIST.length}
                </span>
              </div>
              {/* Select All / Clear All */}
              <div className="flex items-center gap-2" data-testid="checklist-bulk-controls">
                {!allChecked && (
                  <button
                    type="button"
                    data-testid="checklist-select-all-btn"
                    onClick={selectAll}
                    className="text-[11px] font-medium px-2 py-0.5 rounded transition-colors"
                    style={{ color: "#0176D3" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    Select All
                  </button>
                )}
                {checkedCount > 0 && (
                  <button
                    type="button"
                    data-testid="checklist-clear-all-btn"
                    onClick={clearAll}
                    className="text-[11px] font-medium px-2 py-0.5 rounded transition-colors"
                    style={{ color: "#706E6B" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#F3F3F3"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>

            {/* ── Partial-selection indicator ──────────────────────────── */}
            {isIndeterminate && (
              <div
                data-testid="checklist-partial-indicator"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg mb-2"
                style={{ background: "#FEF9EC", border: "1px solid #FDE68A" }}
              >
                <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: "#D97706" }} />
                <span className="text-[11px]" style={{ color: "#92400E" }}>
                  {checkedCount} of {INTENT_CHECKLIST.length} items confirmed — review remaining items before proceeding
                </span>
              </div>
            )}

            {/* ── Individual checklist items ────────────────────────────── */}
            <div className="space-y-1.5">
              {INTENT_CHECKLIST.map(c => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`checklist-${c.id}`}
                  onClick={() => toggleCheck(c.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-xs text-left transition-all ${
                    checklist[c.id]
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-white border-[#E5E5E5] text-[#706E6B] hover:bg-[#FAFAF9]"
                  }`}
                >
                  {checklist[c.id]
                    ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />
                    : <div className="w-3.5 h-3.5 rounded-full border-2 border-[#D5D5D5] flex-shrink-0" />
                  }
                  {c.label}
                </button>
              ))}
            </div>

            {/* ── "All not checked" warning ────────────────────────────── */}
            {status === "VALIDATED" && !allChecked && checkedCount === 0 && (
              <p className="text-[11px] text-amber-600 mt-1.5">
                All checklist items must be confirmed to mark as Validated.
              </p>
            )}

            {/* ── Reviewer acknowledgement gate (governance) ───────────── */}
            {allChecked && (
              <div
                data-testid="checklist-review-acknowledgement"
                className="mt-3 pt-3"
                style={{ borderTop: "1px dashed #D1FAE5" }}
              >
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    data-testid="review-acknowledge-checkbox"
                    checked={reviewAcknowledged}
                    onChange={e => setReviewAcknowledged(e.target.checked)}
                    className="mt-0.5 w-4 h-4 flex-shrink-0"
                    style={{ accentColor: "#22C55E" }}
                  />
                  <span className="text-[11px] leading-relaxed" style={{ color: "#3A3835" }}>
                    I confirm I have independently reviewed each item above against the customer's
                    submitted requirements before recording this verification.
                  </span>
                </label>
                {!reviewAcknowledged && status === "VALIDATED" && (
                  <p
                    data-testid="acknowledge-required-msg"
                    className="text-[11px] mt-1.5 ml-6.5"
                    style={{ color: "#D97706" }}
                  >
                    Reviewer acknowledgement is required before saving as Validated.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Decision */}
          <div>
            <p className="text-xs font-semibold text-[#3A3835] mb-2">Decision</p>
            <div className="flex flex-col gap-2">
              {[
                { value:"VALIDATED",             label:"Validated",             color:"emerald", desc:"All requirements confirmed and accurate." },
                { value:"CLARIFICATION_REQUIRED", label:"Clarification Required", color:"amber",   desc:"Some details need further confirmation." },
                { value:"NOT_PROCEEDING",         label:"Not Proceeding",         color:"red",     desc:"Customer is not moving forward." },
              ].map(o => (
                <button
                  key={o.value}
                  type="button"
                  data-testid={`intent-status-${o.value.toLowerCase()}`}
                  onClick={() => setStatus(o.value)}
                  className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all ${
                    status === o.value
                      ? `bg-${o.color}-50 border-${o.color}-200`
                      : "bg-white border-[#E5E5E5] hover:bg-[#FAFAF9]"
                  }`}
                >
                  <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    status === o.value ? `bg-${o.color}-500 border-${o.color}-500` : "border-[#D5D5D5]"
                  }`}>
                    {status === o.value && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${status === o.value ? `text-${o.color}-700` : "text-[#3A3835]"}`}>{o.label}</p>
                    <p className="text-[11px] text-[#706E6B]">{o.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-[#3A3835]">
              Notes {notesRequired && <span className="text-red-500">*</span>}
            </label>
            <textarea
              data-testid="validate-intent-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder={
                status === "CLARIFICATION_REQUIRED" ? "Describe what needs clarification…" :
                status === "NOT_PROCEEDING"          ? "Reason the lead is not proceeding…" :
                "Optional notes about the validation…"
              }
              className="mt-1.5 w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]/30 resize-none"
            />
            {notesRequired && !notes.trim() && (
              <p className="text-[11px] text-red-500 mt-1">Notes are required for this decision.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#F3F3F3] bg-[#FAFAF9]">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:bg-[#F3F3F3] transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="validate-intent-submit-btn"
            className={`text-sm px-5 py-2 rounded-lg font-semibold transition-all ${
              canSubmit
                ? "text-white hover:opacity-90 shadow-sm"
                : "bg-[#E5E5E5] text-[#B0AEAC] cursor-not-allowed"
            }`}
            style={canSubmit ? { background:"var(--brand-gold)" } : {}}
          >
            {submitting ? (
              <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</span>
            ) : "Save Validation"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Edit Event Intent Modal ─────────────────────────────────────────────────
/**
 * D01/D02 — Resolve a raw event_type_interest value (from any source: manual entry,
 * concierge form "o_WEDDING", BME "WEDDING", legacy "Wedding", etc.) to the canonical
 * code stored in the tenant's event_types master data.
 *
 * Priority: exact code match → exact name match → code without "ET-" prefix →
 * code/name after stripping "o_" and normalising underscores.
 * Returns the original raw value unchanged if no tenant code matches, so the
 * dropdown shows blank rather than silently picking the wrong type.
 */
function findEventTypeCode(raw, eventTypes) {
  if (!raw || !eventTypes.length) return raw || "";
  // 1. Exact code match (e.g. "ET-WEDDING", "CONFERENCE")
  const byCode = eventTypes.find(t => t.code === raw);
  if (byCode) return byCode.code;
  // 2. Exact display-name match (e.g. "Wedding", "Conference")
  const byName = eventTypes.find(t => t.name === raw);
  if (byName) return byName.code;
  // 3. Normalise: strip "o_" concierge prefix, underscore→space, uppercase
  const stripped = raw.toUpperCase().replace(/^O_/, "").replace(/_/g, " ").trim();
  // 3a. Code with "ET-" prefix removed (e.g. "WEDDING" matches "ET-WEDDING")
  const noPrefix = eventTypes.find(
    t => t.code.replace(/^ET-/, "").replace(/_/g, " ") === stripped,
  );
  if (noPrefix) return noPrefix.code;
  // 3b. Code normalised to spaces (e.g. "BIRTHDAY PARTY" matches "BIRTHDAY_PARTY")
  const codeNorm = eventTypes.find(
    t => t.code.replace(/_/g, " ").toUpperCase() === stripped,
  );
  if (codeNorm) return codeNorm.code;
  // 3c. Name normalised (e.g. "AWARD CEREMONY" matches "Award Ceremony")
  const nameNorm = eventTypes.find(
    t => t.name.toUpperCase() === stripped,
  );
  if (nameNorm) return nameNorm.code;
  // No match — return as-is so the RM sees an unset dropdown and must choose explicitly
  return raw;
}

function EditEventIntentModal({ lead, onClose, onUpdated }) {
  const iCls = "w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]/30";

  const toDateStr = (val) => {
    if (!val) return "";
    try { return new Date(val).toISOString().split("T")[0]; } catch { return ""; }
  };

  // D02: For BME / concierge-originated leads the canonical event data lives in the
  // event_intent sub-document (top-level fields may be raw concierge codes or absent).
  // Prefer RM-confirmed top-level values; fall back to the event_intent sub-doc.
  const ei = lead.event_intent || {};

  const [form, setForm] = useState({
    event_type_interest: lead.event_type_interest || ei.event_type || lead.event_type || "",
    planning_scope:      lead.planning_scope || "END_TO_END",
    event_date:          toDateStr(lead.tentative_event_date || lead.event_date || ei.event_date),
    location:            lead.location || lead.event_location || "",
    guest_count:         lead.guest_count ? String(lead.guest_count) : "",
    budget_range:        lead.budget_range || "",
    venue_status:        lead.venue_status || "",
    service_interests:   lead.service_interests || lead.services_interested_in || [],
    notes:               lead.notes || lead.description || lead.inquiry_text || "",
  });
  const [saving,     setSaving]     = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [services,   setServices]   = useState([]);
  const [pathConfig, setPathConfig]  = useState(null);

  useEffect(() => {
    Promise.all([
      eventModelApi.listEventTypes(),
      eventModelApi.listServiceCategories(),
      engageApi.getLeadPathConfig(),
    ]).then(([et, sc, pc]) => {
      const active = (et.data || []).filter(t => t.status === "ACTIVE" || t.is_active);
      setEventTypes(active);
      setServices(sc.data || []);
      setPathConfig(pc.data);
      // D01: Once the tenant event-type master data is loaded, normalise the raw
      // event_type_interest value (could be "WEDDING", "o_wedding", "Wedding", etc.)
      // to the canonical code ("ET-WEDDING") so the dropdown pre-selects correctly.
      setForm(prev => ({
        ...prev,
        event_type_interest: findEventTypeCode(prev.event_type_interest, active),
      }));
    }).catch(() => {});
  }, []);

  const venueStatuses = pathConfig?.venue_statuses || [
    { code: "SEARCHING",    label: "Searching" },
    { code: "SHORTLISTED",  label: "Shortlisted" },
    { code: "CONFIRMED",    label: "Confirmed" },
    { code: "NOT_REQUIRED", label: "Not Required" },
  ];

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleService = (name) => setForm(p => ({
    ...p,
    service_interests: p.service_interests.includes(name)
      ? p.service_interests.filter(x => x !== name)
      : [...p.service_interests, name],
  }));

  const submit = async () => {
    setSaving(true);
    try {
      const patch = {
        event_type_interest: form.event_type_interest || undefined,
        planning_scope:      form.planning_scope || undefined,
        event_date:          form.event_date ? new Date(form.event_date).toISOString() : undefined,
        location:            form.location || undefined,
        guest_count:         form.guest_count ? Number(form.guest_count) : undefined,
        budget_range:        form.budget_range || undefined,
        venue_status:        form.venue_status || undefined,
        service_interests:   form.service_interests.length > 0 ? form.service_interests : undefined,
        notes:               form.notes || undefined,
      };
      // Remove undefined keys
      Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
      if (Object.keys(patch).length === 0) { toast.info("No changes to save"); onClose(); return; }
      await leadApi.update(lead.id, patch);
      toast.success("Event details updated");
      onUpdated();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit Event Intent" onClose={onClose}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">

        {/* Event Type + Planning Scope */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Event Type</label>
            <select
              data-testid="intent-event-type"
              value={form.event_type_interest}
              onChange={e => set("event_type_interest", e.target.value)}
              className={iCls}
            >
              <option value="">— Select —</option>
              {eventTypes.length > 0
                ? eventTypes.map(t => <option key={t.id} value={t.code}>{t.name}</option>)
                : <option value={form.event_type_interest || ""}>{form.event_type_interest || "— select event type —"}</option>
              }
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Planning Scope</label>
            <select
              data-testid="intent-planning-scope"
              value={form.planning_scope}
              onChange={e => set("planning_scope", e.target.value)}
              className={iCls}
            >
              <option value="END_TO_END">End-to-End</option>
              <option value="PARTIAL">Partial / Custom</option>
            </select>
          </div>
        </div>

        {/* Date + Location */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">
              Tentative Date <span className="text-[#706E6B] font-normal">(if known)</span>
            </label>
            <input
              data-testid="intent-event-date"
              type="date"
              value={form.event_date}
              onChange={e => set("event_date", e.target.value)}
              className={iCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Location / City</label>
            <input
              data-testid="intent-location"
              value={form.location}
              onChange={e => set("location", e.target.value)}
              placeholder="e.g. Hyderabad"
              className={iCls}
            />
          </div>
        </div>

        {/* Guest Count + Budget Range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">
              Guest Count <span className="text-[#706E6B] font-normal">(if known)</span>
            </label>
            <input
              data-testid="intent-guest-count"
              type="number"
              min="1"
              value={form.guest_count}
              onChange={e => set("guest_count", e.target.value)}
              placeholder="e.g. 500"
              className={iCls}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">
              Budget Range <span className="text-[#706E6B] font-normal">(if known)</span>
            </label>
            <input
              data-testid="intent-budget-range"
              value={form.budget_range}
              onChange={e => set("budget_range", e.target.value)}
              placeholder="e.g. ₹10L – ₹25L"
              className={iCls}
            />
          </div>
        </div>

        {/* Venue Status */}
        <div>
          <label className="block text-xs font-medium text-[#444] mb-1">Venue Status</label>
          <select
            data-testid="intent-venue-status"
            value={form.venue_status}
            onChange={e => set("venue_status", e.target.value)}
            className={iCls}
          >
            <option value="">— Not specified —</option>
            {venueStatuses.map(v => (
              <option key={v.code} value={v.code}>{v.label}</option>
            ))}
          </select>
        </div>

        {/* Services of Interest */}
        {services.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-[#444] mb-2">Services of Interest</label>
            <div className="flex flex-wrap gap-2">
              {services.map(s => {
                const active = form.service_interests.includes(s.name);
                return (
                  <button
                    key={s.id || s.name}
                    type="button"
                    data-testid={`intent-service-${(s.name || "").toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toggleService(s.name)}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                      active
                        ? "bg-[#EEF4FF] border-[#0176D3]/30 text-[#0176D3]"
                        : "border-[#E5E5E5] text-[#706E6B] hover:border-slate-400"
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Notes / Requirements */}
        <div>
          <label className="block text-xs font-medium text-[#444] mb-1">Customer Requirements / Notes</label>
          <textarea
            data-testid="intent-notes"
            value={form.notes}
            onChange={e => set("notes", e.target.value)}
            rows={3}
            placeholder="Any specific requirements from the customer..."
            className={`${iCls} resize-none`}
          />
        </div>

        {/* Save */}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B]">Cancel</button>
          <button
            data-testid="intent-save-btn"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Event Details"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AssignPocModal({ users, leadId, onClose, onDone }) {
  const [ownerId, setOwnerId] = useState("");
  const [reason,  setReason]  = useState("");
  const [loading, setLoading] = useState(false);

  // Only show Relationship Managers — enforce role boundary in the UI
  const eligibleUsers = users.filter(u => u.role === "RELATIONSHIP_MANAGER");

  const submit = async () => {
    if (!ownerId) { toast.error("Select a Relationship Manager"); return; }
    setLoading(true);
    try {
      await leadApi.assignOwner(leadId, { owner_id: ownerId, reason });
      toast.success("Relationship Manager assigned");
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Assign Relationship Manager" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Select Relationship Manager">
          <select value={ownerId} onChange={e => setOwnerId(e.target.value)} className={inputCls}>
            <option value="">— Select Relationship Manager —</option>
            {eligibleUsers.length === 0 && (
              <option disabled>No Relationship Managers available</option>
            )}
            {eligibleUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Reason (optional)">
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Assignment reason..." className={inputCls} />
        </Field>
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Assign" />
      </div>
    </Modal>
  );
}

function EscalateModal({ leadId, onClose, onDone }) {
  const [reason,   setReason]   = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [loading,  setLoading]  = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Escalation reason required"); return; }
    setLoading(true);
    try {
      await leadApi.escalate(leadId, { reason, priority });
      toast.warning("Lead escalated");
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Escalate Lead" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Priority">
          <select value={priority} onChange={e => setPriority(e.target.value)} className={inputCls}>
            {["NORMAL","HIGH","URGENT"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Reason *">
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="Describe the escalation reason..." className={`${inputCls} resize-none`} />
        </Field>
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Escalate" variant="danger" />
      </div>
    </Modal>
  );
}

function StatusChangeModal({ lead, pathConfig, onClose, onDone }) {
  // Valid transitions from API config, not hardcoded
  const transitions = pathConfig?.valid_transitions || {};
  const allowed     = transitions[lead.status] || [];
  const [newStatus, setNewStatus] = useState(allowed[0] || "");
  const [loading,   setLoading]   = useState(false);

  const submit = async () => {
    if (!newStatus) { toast.error("Select a status"); return; }
    setLoading(true);
    try {
      await leadApi.transition(lead.id, newStatus);
      toast.success(`Status → ${newStatus}`);
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Change Status" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-[#706E6B]">
          Current: <span className="text-[#444444] font-mono">{lead.status}</span>
        </p>
        {allowed.length === 0 ? (
          <p className="text-sm text-[#706E6B]">No valid transitions from {lead.status}</p>
        ) : (
          <Field label="Transition to">
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className={inputCls}>
              {allowed.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        )}
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Change Status" disabled={allowed.length === 0} />
      </div>
    </Modal>
  );
}

function AddMeetingModal({ leadId, onClose, onDone, prefill }) {
  const [form, setForm] = useState({ title: prefill?.title || "", due_date: prefill?.due_date || "", notes: prefill?.notes || "", task_type: "MEETING" });
  const [loading, setLoading] = useState(false);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [agendaItems, setAgendaItems] = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSuggestAgenda = async () => {
    setAgendaLoading(true);
    try {
      const { data } = await recommendationApi.suggestAgenda(leadId, form.title || "Discovery meeting");
      setAgendaItems(data.agenda_items || []);
      const agendaText = (data.agenda_items || []).map((a, i) => `${i + 1}. ${a}`).join("\n");
      set("notes", (form.notes ? form.notes + "\n\nSuggested agenda:\n" : "Suggested agenda:\n") + agendaText);
      toast.success("Agenda suggestion added — review and edit");
    } catch { toast.error("Could not generate agenda suggestion"); }
    finally { setAgendaLoading(false); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Meeting title is required"); return; }
    if (!form.due_date) { toast.error("Meeting date / time is required"); return; }
    setLoading(true);
    try {
      await taskApi.create(leadId, form);
      toast.success("Meeting scheduled");
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Schedule Meeting" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Meeting Title *">
          <input value={form.title} onChange={e => set("title", e.target.value)}
            placeholder="Discovery call, Site visit, Planning review…" className={inputCls} data-testid="meeting-title" />
        </Field>
        <Field label="Meeting Date & Time *">
          <input type="datetime-local" value={form.due_date} onChange={e => set("due_date", e.target.value)}
            className={inputCls} data-testid="meeting-datetime" />
        </Field>
        <Field label="Notes / Agenda (optional)">
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3}
            placeholder="Agenda, location, attendees…" className={`${inputCls} resize-none`} data-testid="meeting-notes" />
        </Field>
        {/* AI agenda suggestion */}
        <button onClick={handleSuggestAgenda} disabled={agendaLoading} data-testid="ai-agenda-btn"
          className="flex items-center gap-1.5 text-xs text-[#C9A15F] hover:text-[#B8904E] disabled:opacity-60 transition-colors">
          {agendaLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {agendaLoading ? "Generating agenda…" : "Suggest agenda with AI"}
        </button>
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Schedule Meeting" />
      </div>
    </Modal>
  );
}

function AddNoteModal({ leadId, onClose, onDone, prefill }) {
  const [form, setForm] = useState({ title: prefill?.title || "", notes: prefill?.notes || "", task_type: "NOTE" });
  const [loading, setLoading] = useState(false);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structured, setStructured] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleStructure = async () => {
    if (!form.notes.trim()) { toast.error("Enter notes first to structure them"); return; }
    setStructureLoading(true);
    try {
      const { data } = await recommendationApi.structureNotes(leadId, form.notes);
      setStructured(data.structured_notes || {});
      toast.success("Notes structured — review before saving");
    } catch { toast.error("Could not structure notes"); }
    finally { setStructureLoading(false); }
  };

  const submit = async () => {
    if (!form.notes.trim()) { toast.error("Note content is required"); return; }
    setLoading(true);
    try {
      await taskApi.create(leadId, form);
      toast.success("Note saved");
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Modal title="Add Note" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Title (optional)">
          <input value={form.title} onChange={e => set("title", e.target.value)}
            placeholder="Note subject…" className={inputCls} data-testid="note-title" />
        </Field>
        <Field label="Note *">
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={4}
            placeholder="Write your note here… AI can help structure it." className={`${inputCls} resize-none`} data-testid="note-content" />
        </Field>
        {/* AI structure */}
        <button onClick={handleStructure} disabled={structureLoading || !form.notes.trim()} data-testid="ai-structure-notes-btn"
          className="flex items-center gap-1.5 text-xs text-[#C9A15F] hover:text-[#B8904E] disabled:opacity-60 transition-colors">
          {structureLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {structureLoading ? "Structuring…" : "Structure notes with AI"}
        </button>
        {/* Structured output preview */}
        {structured && Object.keys(structured).length > 0 && (
          <div className="p-2.5 bg-[#F9F9F7] border border-[#E5E5E5] rounded-lg space-y-1.5">
            <p className="text-[10px] font-semibold text-[#706E6B] uppercase tracking-wide flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#C9A15F]" /> AI structure — requires explicit review
            </p>
            {Object.entries(structured).filter(([, v]) => Array.isArray(v) && v.length > 0).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[10px] font-semibold text-[#444444] capitalize">{cat.replace(/_/g, " ")}</p>
                <ul className="text-[11px] text-[#706E6B] list-disc pl-3 space-y-0.5">
                  {items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
            <p className="text-[10px] text-amber-600">AI output. Verify before treating as confirmed requirements.</p>
          </div>
        )}
        <ModalActions onClose={onClose} onSubmit={submit} loading={loading} label="Save Note" />
      </div>
    </Modal>
  );
}

// ─── Shared UI Primitives ──────────────────────────────────────────────────────

const inputCls = "w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#F3F3F3]/80 p-0 sm:p-4">
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-t-2xl sm:rounded-xl w-full sm:max-w-sm space-y-4 p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#181818] font-sans">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444] min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#706E6B] mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ onClose, onSubmit, loading, label, variant = "primary", disabled = false }) {
  return (
    <div className="flex gap-3 pt-1">
      <button onClick={onClose}
        className="flex-1 px-4 py-2 border border-[#E5E5E5] rounded-lg text-sm text-[#706E6B] hover:text-[#181818] transition-colors">
        Cancel
      </button>
      <button onClick={onSubmit} disabled={loading || disabled}
        className={`flex-1 px-4 py-2 rounded-lg text-sm disabled:opacity-60 transition-all flex items-center justify-center gap-2 ${
          variant === "danger"
            ? "bg-rose-500/10 border border-rose-500/30 text-rose-400 hover:bg-rose-500/20"
            : "bg-[#EEF4FF] border border-[#0176D3]/30 text-[#0176D3] hover:bg-[#EEF4FF]"
        }`}>
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {loading ? "..." : label}
      </button>
    </div>
  );
}

function Panel({ title, icon: Icon, children, action }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#0176D3]" />
          <h3 className="text-sm font-semibold text-[#181818] font-sans">{title}</h3>
        </div>
        {action}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function InfoPair({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[10px] text-[#706E6B] uppercase tracking-wider flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-xs text-[#444444] text-right ${mono ? "font-mono" : ""}`}>{String(value)}</span>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="p-4 space-y-4">
      <div className="h-28 bg-[#F3F3F3] rounded-xl animate-pulse" />
      <div className="h-10 bg-[#F3F3F3] rounded-xl animate-pulse" />
      <div className="h-64 bg-[#F3F3F3] rounded-xl animate-pulse" />
    </div>
  );
}

function EmptyState({ message, onBack }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-[#706E6B]">
      <p>{message}</p>
      {onBack && <button onClick={onBack} className="text-[#0176D3] text-sm hover:underline">← Back to Leads</button>}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
  } catch { return iso; }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7)   return `${days}d ago`;
    return fmtDate(iso);
  } catch { return iso; }
}


// ─── Duplicate Resolution Modal ────────────────────────────────────────────────
function ResolutionModal({ ctx, onResolve, onClose }) {
  const label = ctx.resolution_type === "ACCOUNT" ? "Account" : "Contact";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#F3F3F3]/85 p-4">
      <div className="bg-white border border-[#E5E5E5] rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-semibold text-[#181818]">Multiple {label}s Found</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-[#706E6B]">{ctx.message || `Select the correct ${label} to link with this lead.`}</p>
          {(ctx.candidates || []).map((c) => (
            <button
              key={c.id}
              onClick={() => onResolve(c.id)}
              className="w-full text-left px-4 py-3 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg hover:border-[#0176D3]/30 transition-colors"
            >
              <p className="text-sm text-[#181818] font-medium">{c.name}</p>
              {c.email && <p className="text-xs text-[#706E6B] mt-0.5">{c.email}</p>}
              {c.phone && <p className="text-xs text-[#706E6B]">{c.phone}</p>}
            </button>
          ))}
          <button
            onClick={onClose}
            className="w-full text-center text-xs text-[#706E6B] hover:text-[#444444] py-2"
          >
            Cancel — keep existing details
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Handoff Blocked Modal ─────────────────────────────────────────────────────

function HandoffBlockedModal({ ctx, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        data-testid="handoff-blocked-modal"
        className="bg-white rounded-xl border border-rose-200 w-full max-w-md shadow-2xl"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-500" />
            <h3 className="text-sm font-semibold text-[#181818]">Cannot Start Event Planning</h3>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-[#706E6B]">
            This lead is missing required handoff information. Complete the following fields before converting to an event:
          </p>
          <ul className="space-y-1.5">
            {(ctx.missing_fields || []).map((field) => (
              <li key={field} className="flex items-center gap-2 text-xs text-rose-600">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                {field}
              </li>
            ))}
          </ul>
          <p className="text-xs text-[#706E6B] pt-1 border-t border-[#E5E5E5]">
            Update the lead's event intent and customer details, then try again.
          </p>
          <button
            data-testid="handoff-blocked-close-btn"
            onClick={onClose}
            className="w-full mt-2 px-4 py-2 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-sm text-[#0176D3] hover:bg-blue-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
